import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Building2, 
  Users, 
  ShieldCheck, 
  Plus, 
  Trash2, 
  Edit2, 
  Save, 
  X,
  ChevronRight,
  LayoutDashboard,
  ArrowRightLeft,
  Wallet,
  FileText,
  Settings,
  ShoppingCart,
  Package,
  Users as UsersIcon,
  Receipt,
  FileCheck,
  Truck,
  BookOpen,
  HelpCircle,
  TrendingUp,
  History,
  Search,
  Tag,
  CheckCircle2,
  MapPin,
  Mail,
  Check,
  Key,
  Laptop,
  AlertCircle,
  Activity,
  Loader2,
  Sparkles
} from 'lucide-react';
import { db, handleFirestoreError, OperationType } from '../firebase';
import GoogleMapReact from 'google-map-react';
import { collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  onSnapshot, 
  query, 
  orderBy,
  where,
  limit,
  serverTimestamp,
  setDoc,
  Timestamp,
  deleteField } from '../firebase';
import { notify } from '../services/notify';
import { MODULES, COUNTRIES, CITY_MAP } from '../constants';
import { getNames } from 'country-list';
import { cn } from '../lib/utils';
const countries = getNames();

const getLocalAuthToken = () =>
  typeof window === 'undefined' ? null : localStorage.getItem('app_auth_token');

const isLocalSession = () => Boolean(getLocalAuthToken());

const localAdminRequest = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
  const token = getLocalAuthToken();
  if (!token) throw new Error('Local session token is unavailable');
  const response = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.error || `Request failed (${response.status})`);
  return payload as T;
};

interface Organization {
  id: string;
  name: string;
  country: string;
  city?: string;
  industry: string;
  targetAudience: string;
  status: 'Active' | 'Inactive' | 'Suspended' | 'On Hold';
  assignedModules?: string[];
  keyPersonId?: string;
  licenseKey?: string;
  location?: {
    lat: number;
    lng: number;
    address: string;
  };
  subscription?: {
    productId?: string;
    packageId: string;
    purchaseDate: string;
    warningDate: string;
    renewalDate: string;
  };
  createdAt: any;
}

interface OrgMembership {
  orgId: string;
  role: 'admin' | 'user' | 'super-admin' | 'owner';
  permissions: string[];
  suspended?: boolean;
}

interface OrgUser {
  id: string;
  fullName: string;
  email: string;
  nationality?: string;
  countryCode?: string;
  phoneNumber?: string;
  gender?: 'male' | 'female';
  memberships: OrgMembership[];
  suspended?: boolean; // Global suspension
  createdAt: any;
}

interface ProductPackage {
  name: string;
  price: number;
  description: string;
  modules: string[];
}

interface ProductService {
  id: string;
  name: string;
  description: string;
  type: 'Product' | 'Service';
  packages: ProductPackage[];
  createdAt: any;
}

// Helper to generate a secure key
function generateSecureKey() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; 
  const generateSegment = () => {
    let segment = "";
    const array = new Uint32Array(4);
    window.crypto.getRandomValues(array);
    for (let i = 0; i < 4; i++) {
      segment += chars[array[i] % chars.length];
    }
    return segment;
  };
  return `${generateSegment()}-${generateSegment()}-${generateSegment()}-${generateSegment()}`;
}

const SuperAdminView: React.FC = () => {
  const [activeSubModule, setActiveSubModule] = useState<'organizations' | 'users' | 'products' | 'licenses' | 'ai_settings'>('organizations');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [aiSettings, setAiSettings] = useState({
    provider: 'openai-compatible',
    baseUrl: 'https://api.openai.com/v1',
    model: 'gpt-4o-mini',
    apiKey: '',
    apiKeyLast4: null as string | null,
    enabled: false,
  });
  const [isLoadingAiSettings, setIsLoadingAiSettings] = useState(false);
  const [isSavingAiSettings, setIsSavingAiSettings] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [selectedOrgProfile, setSelectedOrgProfile] = useState<Organization | null>(null);
  const [selectedProductProfile, setSelectedProductProfile] = useState<ProductService | null>(null);
  const [isEditingModules, setIsEditingModules] = useState(false);
  const [isEditingOrgDetails, setIsEditingOrgDetails] = useState(false);
  const [isEditingBillingDetails, setIsEditingBillingDetails] = useState(false);

  // License State
  const [licenses, setLicenses] = useState<any[]>([]);
  const [isAddingLicense, setIsAddingLicense] = useState(false);
  const [editingLicense, setEditingLicense] = useState<any | null>(null);
  const [viewingActivityLicense, setViewingActivityLicense] = useState<any | null>(null);
  const [licenseActivities, setLicenseActivities] = useState<any[]>([]);
  const [licenseToReset, setLicenseToReset] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newLicense, setNewLicense] = useState<{
    key?: string;
    expiryDays: number;
    deviceLimit: number;
    permissions: string[];
    status?: string;
    orgId: string;
    isOffline: boolean;
  }>({
    expiryDays: 365,
    deviceLimit: 1,
    permissions: ['all'],
    orgId: '',
    isOffline: false
  });

  // Email Composer State
  const [isEmailComposerOpen, setIsEmailComposerOpen] = useState(false);
  const [emailDetails, setEmailDetails] = useState({
    to: '',
    cc: '',
    subject: '',
    body: ''
  });

  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
  const [products, setProducts] = useState<ProductService[]>([]);
  
  // Organization Form State
  const [isAddingOrg, setIsAddingOrg] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [newOrg, setNewOrg] = useState({
    name: '',
    country: '',
    city: '',
    industry: '',
    targetAudience: '',
    status: 'Active' as const,
    assignedModules: [] as string[],
    licenseKey: '',
    location: { lat: 25.2048, lng: 55.2708, address: '' }, // Default to Dubai
    subscription: { productId: '', packageId: '', purchaseDate: '', warningDate: '', renewalDate: '' }
  });

  const [isMapModalOpen, setIsMapModalOpen] = useState(false);

  // User Form State
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [editingUser, setEditingUser] = useState<OrgUser | null>(null);
  const [newUser, setNewUser] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    nationality: '',
    countryCode: '+20',
    gender: 'male' as 'male' | 'female',
    password: '',
    memberships: [] as OrgMembership[]
  });
  const [currentMembership, setCurrentMembership] = useState<OrgMembership>({
    orgId: '',
    role: 'user',
    permissions: ['dashboard']
  });

  // Product Form State
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductService | null>(null);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    type: 'Product' as const,
    packages: [{ name: '', price: 0, description: '', modules: [] }]
  });

  useEffect(() => {
    if (activeSubModule !== 'ai_settings' || !isLocalSession()) return;
    let cancelled = false;
    setIsLoadingAiSettings(true);
    void localAdminRequest<any>('/api/admin/ai-settings')
      .then(settings => {
        if (cancelled) return;
        setAiSettings(current => ({
          ...current,
          provider: settings.provider || current.provider,
          baseUrl: settings.baseUrl || current.baseUrl,
          model: settings.model || current.model,
          apiKey: '',
          apiKeyLast4: settings.apiKeyLast4 || null,
          enabled: Boolean(settings.enabled),
        }));
      })
      .catch(error => {
        if (!cancelled) notify.error(error?.message || 'Failed to load AI settings');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingAiSettings(false);
      });
    return () => { cancelled = true; };
  }, [activeSubModule]);

  useEffect(() => {
    if (isLocalSession()) {
      let cancelled = false;
      void localAdminRequest<Organization[]>('/api/admin/organizations')
        .then(organizations => { if (!cancelled) setOrganizations(organizations); })
        .catch(error => { if (!cancelled) { console.error('Local super-admin organization load failed:', error); notify.error('Failed to load organizations'); } });
      return () => { cancelled = true; };
    }

    const q = query(collection(db, 'organizations'), orderBy('name', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setOrganizations(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Organization)));
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'organizations');
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (isLocalSession()) {
      let cancelled = false;
      void localAdminRequest<ProductService[]>('/api/admin/products')
        .then(products => { if (!cancelled) setProducts(products); })
        .catch(error => { if (!cancelled) { console.error('Local super-admin product load failed:', error); notify.error('Failed to load products'); } });
      return () => { cancelled = true; };
    }

    const q = query(collection(db, 'super_products'), orderBy('name', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ProductService)));
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'super_products');
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (isLocalSession()) {
      let cancelled = false;
      void localAdminRequest<OrgUser[]>('/api/admin/users')
        .then(users => { if (!cancelled) setOrgUsers(users); })
        .catch(error => { if (!cancelled) { console.error('Local super-admin user load failed:', error); notify.error('Failed to load users'); } });
      return () => { cancelled = true; };
    }

    const q = query(
      collection(db, 'org_users'), 
      orderBy('email', 'asc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setOrgUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as OrgUser)));
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'org_users');
    });
    return () => unsubscribe();
  }, []);

  // Fetch licenses
  useEffect(() => {
    if (activeSubModule === 'licenses' || activeSubModule === 'organizations') {
      if (isLocalSession()) {
        let cancelled = false;
        void localAdminRequest<any[]>('/api/admin/licenses')
          .then(licenses => { if (!cancelled) setLicenses(licenses); })
          .catch(error => { if (!cancelled) { console.error('Local super-admin license load failed:', error); notify.error('Failed to load licenses'); } });
        return () => { cancelled = true; };
      }

      const q = query(collection(db, 'licenses'), orderBy('createdAt', 'desc'));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        setLicenses(snapshot.docs.map(document => {
           const data = document.data();
           return {
             id: document.id,
             key: data.key,
             deviceLimit: data.deviceLimit,
             orgId: data.orgId,
             status: data.status,
             devices: data.devices,
             featurePermissions: data.featurePermissions,
             expiryDate: data.expiryDate?.toDate()?.toISOString(),
             createdAt: data.createdAt?.toDate()?.toISOString(),
             updatedAt: data.updatedAt?.toDate()?.toISOString(),
           };
        }));
      }, (error) => {
        console.error("License list error:", error);
      });
      return () => unsubscribe();
    }
  }, [activeSubModule]);

  useEffect(() => {
    if (viewingActivityLicense) {
      if (isLocalSession()) {
        let cancelled = false;
        void localAdminRequest<any[]>(`/api/admin/licenses/${viewingActivityLicense.id}/activities`)
          .then(activities => { if (!cancelled) setLicenseActivities(activities); })
          .catch(error => { if (!cancelled) console.error('Local license activity load failed:', error); });
        return () => { cancelled = true; };
      }

      const q = query(
        collection(db, 'licenses', viewingActivityLicense.key, 'activities'),
        orderBy('timestamp', 'desc'),
        limit(50)
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        setLicenseActivities(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }, (error) => {
        console.error("Activity log error:", error);
      });
      return () => unsubscribe();
    }
  }, [viewingActivityLicense]);

  const handleGenerateLicense = async () => {
    try {
      if (isLocalSession()) {
        if (editingLicense) {
          await localAdminRequest(`/api/admin/licenses/${editingLicense.id}`, {
            method: 'PATCH',
            body: JSON.stringify({
              deviceLimit: Number(newLicense.deviceLimit) || 1,
              featurePermissions: newLicense.permissions || ['all'],
              orgId: newLicense.orgId || null,
              isOffline: newLicense.isOffline || false,
            }),
          });
          notify.confirm('License updated!');
        } else {
          await localAdminRequest('/api/admin/licenses', {
            method: 'POST',
            body: JSON.stringify({
              key: generateSecureKey(), expiryDays: newLicense.expiryDays,
              deviceLimit: Number(newLicense.deviceLimit) || 1,
              featurePermissions: newLicense.permissions || ['all'],
              orgId: newLicense.orgId || null, isOffline: newLicense.isOffline || false,
            }),
          });
          notify.confirm('License key generated!');
        }
        const licenses = await localAdminRequest<any[]>('/api/admin/licenses');
        setLicenses(licenses);
        setEditingLicense(null);
        setIsAddingLicense(false);
        return;
      }

      if (editingLicense) {
        let expiryDate = new Date();
        if (newLicense.expiryDays !== -1) {
             expiryDate.setDate(new Date(editingLicense.expiryDate).getDate() + (Number(newLicense.expiryDays) || 0)); // Only useful if we wanted to extend. let's just use existing expiryDate for simple edit, or recalculate if needed. Better: let's recalculate from now if they change days, or keep old?
        }
        
        await updateDoc(doc(db, "licenses", editingLicense.key), {
          deviceLimit: Number(newLicense.deviceLimit) || 1,
          featurePermissions: newLicense.permissions || ["all"],
          orgId: newLicense.orgId || null,
          isOffline: newLicense.isOffline || false,
          updatedAt: serverTimestamp(),
        });
        setEditingLicense(null);
        setIsAddingLicense(false);
        notify.confirm("License updated!");
        return;
      }

      const key = generateSecureKey();
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + (Number(newLicense.expiryDays) || 365));

      const licenseData = {
        key,
        status: "Active",
        expiryDate: expiryDate,
        deviceLimit: Number(newLicense.deviceLimit) || 1,
        featurePermissions: newLicense.permissions || ["all"],
        orgId: newLicense.orgId || null,
        isOffline: newLicense.isOffline || false,
        devices: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, "licenses", key), licenseData);
      
      setIsAddingLicense(false);
      notify.confirm("License key generated!");
    } catch (err: any) {
      console.error("Generate error:", err);
      notify.error(err.message || "Failed to generate license");
    }
  };

  const handleToggleLicenseStatus = async (key: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'Active' ? 'Suspended' : 'Active';
      if (isLocalSession()) {
        const license = licenses.find(item => item.key === key);
        if (!license) throw new Error('License not found');
        await localAdminRequest(`/api/admin/licenses/${license.id}`, { method: 'PATCH', body: JSON.stringify({ status: newStatus }) });
        setLicenses(current => current.map(item => item.id === license.id ? { ...item, status: newStatus } : item));
        notify.confirm(`License ${newStatus.toLowerCase()} successfully`);
        return;
      }
      await updateDoc(doc(db, "licenses", key), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      notify.confirm(`License ${newStatus.toLowerCase()} successfully`);
    } catch (err) {
      notify.error("Failed to toggle license status");
    }
  };

  const handleResetLicense = async (key: string) => {
    try {
      if (isLocalSession()) {
        const license = licenses.find(item => item.key === key);
        if (!license) throw new Error('License not found');
        await localAdminRequest(`/api/admin/licenses/${license.id}/reset-devices`, { method: 'POST' });
        setLicenses(current => current.map(item => item.id === license.id ? { ...item, devices: [] } : item));
        setLicenseToReset(null);
        notify.confirm('License reset successfully');
        return;
      }
      await updateDoc(doc(db, "licenses", key), {
        devices: [],
        updatedAt: serverTimestamp()
      });
      notify.confirm("License reset successfully");
      setLicenseToReset(null);
    } catch (err: any) {
      console.error("Reset License Error:", err);
      notify.error("Failed to reset license: " + err.message);
    }
  };

  const handleAddOrg = async () => {
    if (!newOrg.name.trim()) return;
    try {
      if (isLocalSession()) {
        const created = await localAdminRequest<Organization>('/api/admin/organizations', { method: 'POST', body: JSON.stringify(newOrg) });
        if (newOrg.licenseKey) {
          const license = licenses.find(item => item.key === newOrg.licenseKey);
          if (license) await localAdminRequest(`/api/admin/licenses/${license.id}`, { method: 'PATCH', body: JSON.stringify({ orgId: created.id }) });
        }
        setOrganizations(current => [...current, { ...created, licenseKey: newOrg.licenseKey }].sort((a, b) => a.name.localeCompare(b.name)));
        setNewOrg({ name: '', country: '', city: '', industry: '', targetAudience: '', status: 'Active', assignedModules: [], licenseKey: '', location: { lat: 25.2048, lng: 55.2708, address: '' }, subscription: { productId: '', packageId: '', purchaseDate: '', warningDate: '', renewalDate: '' } });
        setIsAddingOrg(false);
        notify.confirm('Organization added successfully');
        return;
      }

      const orgRef = doc(collection(db, 'organizations'));
      const orgId = orgRef.id;

      await setDoc(orgRef, {
        ...newOrg,
        id: orgId,
        createdAt: serverTimestamp()
      });

      // Update license if one was selected
      if (newOrg.licenseKey) {
        const licenseDoc = licenses.find(l => l.key === newOrg.licenseKey);
        if (licenseDoc) {
          await updateDoc(doc(db, 'licenses', licenseDoc.id), {
            orgId: orgId,
            updatedAt: serverTimestamp()
          });
        }
      }

      setNewOrg({
        name: '',
        country: '',
        city: '',
        industry: '',
        targetAudience: '',
        status: 'Active',
        assignedModules: [],
        licenseKey: '',
        location: { lat: 25.2048, lng: 55.2708, address: '' },
        subscription: { productId: '', packageId: '', purchaseDate: '', warningDate: '', renewalDate: '' }
      });
      setIsAddingOrg(false);
      notify.confirm('Organization added successfully');
    } catch (err) {
      handleFirestoreError(err, OperationType.CREATE, 'organizations');
      notify.error('Failed to add organization');
    }
  };

  const handlePackageChange = (packageId: string) => {
    const productId = newOrg.subscription?.productId;
    if (!productId) return;

    const product = products.find(p => p.id === productId);
    const pkg = product?.packages.find(p => p.name === packageId);

    setNewOrg({
      ...newOrg,
      subscription: {
        ...newOrg.subscription!,
        packageId: packageId
      },
      assignedModules: pkg?.modules || []
    });
  };

  const handleUpdateOrg = async () => {
    if (!editingOrg || !newOrg.name.trim()) return;
    try {
      if (isLocalSession()) {
        const updated = await localAdminRequest<Organization>(`/api/admin/organizations/${editingOrg.id}`, { method: 'PATCH', body: JSON.stringify(newOrg) });
        const currentlyLinked = licenses.filter(item => item.orgId === editingOrg.id && item.key !== newOrg.licenseKey);
        await Promise.all(currentlyLinked.map(item => localAdminRequest(`/api/admin/licenses/${item.id}`, { method: 'PATCH', body: JSON.stringify({ orgId: null }) })));
        if (newOrg.licenseKey) {
          const target = licenses.find(item => item.key === newOrg.licenseKey);
          if (target) await localAdminRequest(`/api/admin/licenses/${target.id}`, { method: 'PATCH', body: JSON.stringify({ orgId: editingOrg.id }) });
        }
        setOrganizations(current => current.map(organization => organization.id === updated.id ? { ...updated, licenseKey: newOrg.licenseKey } : organization));
        if (selectedOrgProfile?.id === updated.id) setSelectedOrgProfile({ ...updated, licenseKey: newOrg.licenseKey });
        setEditingOrg(null); setIsAddingOrg(false); setIsEditingOrgDetails(false); setIsEditingBillingDetails(false); setIsEditingModules(false);
        notify.confirm('Organization updated successfully');
        return;
      }
      await updateDoc(doc(db, 'organizations', editingOrg.id), {
        ...newOrg
      });

      // Update license link
      if (newOrg.licenseKey) {
        // Find if any other license is linked to this org and unlink it
        const currentLicense = licenses.find(l => l.orgId === editingOrg.id && l.key !== newOrg.licenseKey);
        if (currentLicense) {
          await updateDoc(doc(db, 'licenses', currentLicense.id), {
             orgId: null,
             updatedAt: serverTimestamp()
          });
        }

        // Link new license
        const targetLicense = licenses.find(l => l.key === newOrg.licenseKey);
        if (targetLicense) {
          await updateDoc(doc(db, 'licenses', targetLicense.id), {
            orgId: editingOrg.id,
            updatedAt: serverTimestamp()
          });
        }
      } else {
        // Unlink any license from this org
        const currentLicense = licenses.find(l => l.orgId === editingOrg.id);
        if (currentLicense) {
          await updateDoc(doc(db, 'licenses', currentLicense.id), {
             orgId: null,
             updatedAt: serverTimestamp()
          });
        }
      }

      setNewOrg({
        name: '',
        country: '',
        city: '',
        industry: '',
        targetAudience: '',
        status: 'Active',
        assignedModules: [],
        licenseKey: '',
        location: { lat: 25.2048, lng: 55.2708, address: '' },
        subscription: { productId: '', packageId: '', purchaseDate: '', warningDate: '', renewalDate: '' }
      });
      setEditingOrg(null);
      setIsAddingOrg(false);
      setIsEditingOrgDetails(false);
      setIsEditingBillingDetails(false);
      setIsEditingModules(false);
      if (selectedOrgProfile?.id === editingOrg.id) {
        setSelectedOrgProfile({ ...editingOrg, ...newOrg });
      }
      notify.confirm('Organization updated successfully');
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, `organizations/${editingOrg.id}`);
      notify.error('Failed to update organization');
    }
  };

  const handleDeleteOrg = async (id: string) => {
    try {
      if (isLocalSession()) {
        await localAdminRequest(`/api/admin/organizations/${id}`, { method: 'DELETE' });
        setOrganizations(current => current.filter(organization => organization.id !== id));
        if (selectedOrg?.id === id) setSelectedOrg(null);
        if (selectedOrgProfile?.id === id) setSelectedOrgProfile(null);
        notify.confirm('Organization deleted');
        return;
      }
      await deleteDoc(doc(db, 'organizations', id));
      if (selectedOrg?.id === id) setSelectedOrg(null);
      notify.confirm('Organization deleted');
    } catch (err) {
      if (isLocalSession()) {
        const message = err instanceof Error ? err.message : 'Failed to delete organization';
        console.error('Local organization deletion failed:', err);
        notify.error(message);
        return;
      }
      handleFirestoreError(err, OperationType.DELETE, `organizations/${id}`);
      notify.error('Failed to delete organization');
    }
  };

  const handleAddUser = async () => {
    if (!newUser.email.trim() || !newUser.fullName.trim() || (newUser.memberships || []).length === 0) {
      notify.error('Full Name, Email, and at least one Organization membership are required');
      return;
    }
    if (isLocalSession() && !editingUser && newUser.password.length < 8) {
      notify.error('Password must be at least 8 characters');
      return;
    }
    if (isLocalSession() && editingUser && newUser.password && newUser.password.length < 8) {
      notify.error('New password must be at least 8 characters');
      return;
    }
    try {
      if (isLocalSession()) {
        const payload = { ...newUser, memberships: newUser.memberships };
        const saved = editingUser
          ? await localAdminRequest<OrgUser>(`/api/admin/users/${editingUser.id}`, { method: 'PATCH', body: JSON.stringify(payload) })
          : await localAdminRequest<OrgUser>('/api/admin/users', { method: 'POST', body: JSON.stringify(payload) });
        setOrgUsers(current => editingUser ? current.map(user => user.id === saved.id ? saved : user) : [...current, saved].sort((a, b) => a.email.localeCompare(b.email)));
        setNewUser({ fullName: '', email: '', phoneNumber: '', nationality: '', countryCode: '+20', gender: 'male', password: '', memberships: [] });
        setCurrentMembership({ orgId: '', role: 'user', permissions: ['dashboard'] });
        setIsAddingUser(false); setEditingUser(null);
        notify.confirm(editingUser ? 'User updated successfully' : 'User created with multiple memberships');
        return;
      }
      if (editingUser) {
        await updateDoc(doc(db, 'org_users', editingUser.id), {
          fullName: newUser.fullName,
          email: newUser.email,
          phoneNumber: newUser.phoneNumber,
          nationality: newUser.nationality,
          countryCode: newUser.countryCode,
          gender: newUser.gender,
          memberships: newUser.memberships,
          updatedAt: serverTimestamp()
        });
        notify.confirm('User updated successfully');
      } else {
        await addDoc(collection(db, 'org_users'), {
          fullName: newUser.fullName,
          email: newUser.email,
          phoneNumber: newUser.phoneNumber,
          memberships: newUser.memberships,
          suspended: false,
          createdAt: serverTimestamp()
        });
        notify.confirm('User created with multiple memberships');
      }
      
      setNewUser({
        fullName: '',
        email: '',
        phoneNumber: '',
        nationality: '',
        countryCode: '+20',
        gender: 'male',
        password: '',
        memberships: []
      });
      setCurrentMembership({
        orgId: '',
        role: 'user',
        permissions: ['dashboard']
      });
      setIsAddingUser(false);
      setEditingUser(null);
    } catch (err) {
      handleFirestoreError(err, OperationType.CREATE, 'org_users');
      notify.error(editingUser ? 'Failed to update user' : 'Failed to create user');
    }
  };

  const handleEditUser = (user: OrgUser) => {
    setEditingUser(user);
    setNewUser({
      fullName: user.fullName || '',
      email: user.email || '',
      phoneNumber: user.phoneNumber || '',
      nationality: user.nationality || '',
      countryCode: user.countryCode || '+20',
      gender: user.gender || 'male',
      password: '',
      memberships: user.memberships || []
    });
    setIsAddingUser(true);
  };

  const handleToggleSuspendUser = async (user: OrgUser) => {
    try {
      if (isLocalSession()) {
        const updated = await localAdminRequest<OrgUser>(`/api/admin/users/${user.id}`, {
          method: 'PATCH', body: JSON.stringify({ ...user, suspended: !user.suspended }),
        });
        setOrgUsers(current => current.map(entry => entry.id === updated.id ? updated : entry));
        notify.confirm(user.suspended ? 'User unsuspended' : 'User suspended');
        return;
      }
      await updateDoc(doc(db, 'org_users', user.id), {
        suspended: !user.suspended,
        updatedAt: serverTimestamp()
      });
      notify.confirm(user.suspended ? 'User unsuspended' : 'User suspended');
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, `org_users/${user.id}`);
      notify.error('Failed to update user status');
    }
  };

  const handleToggleSuspendOrg = async (org: Organization) => {
    try {
      const newStatus = org.status === 'Suspended' ? 'Active' : 'Suspended';
      if (isLocalSession()) {
        const updated = await localAdminRequest<Organization>(`/api/admin/organizations/${org.id}`, { method: 'PATCH', body: JSON.stringify({ status: newStatus }) });
        setOrganizations(current => current.map(entry => entry.id === updated.id ? updated : entry));
        notify.confirm(newStatus === 'Suspended' ? `${org.name} suspended` : `${org.name} activated`);
        return;
      }
      await updateDoc(doc(db, 'organizations', org.id), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      notify.confirm(newStatus === 'Suspended' ? `${org.name} suspended` : `${org.name} activated`);
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, `organizations/${org.id}`);
      notify.error('Failed to update organization status');
    }
  };

  const handleAddProduct = async () => {
    if (!newProduct.name.trim()) return;
    try {
      if (isLocalSession()) {
        const saved = editingProduct
          ? await localAdminRequest<ProductService>(`/api/admin/products/${editingProduct.id}`, { method: 'PATCH', body: JSON.stringify(newProduct) })
          : await localAdminRequest<ProductService>('/api/admin/products', { method: 'POST', body: JSON.stringify(newProduct) });
        setProducts(current => editingProduct ? current.map(product => product.id === saved.id ? saved : product) : [...current, saved].sort((a, b) => a.name.localeCompare(b.name)));
        setNewProduct({ name: '', description: '', type: 'Product', packages: [{ name: '', price: 0, description: '', modules: [] }] });
        setIsAddingProduct(false); setEditingProduct(null);
        notify.confirm(editingProduct ? 'Product updated successfully' : 'Product added successfully');
        return;
      }
      if (editingProduct) {
        await updateDoc(doc(db, 'super_products', editingProduct.id), {
          ...newProduct,
          updatedAt: serverTimestamp()
        });
        notify.confirm('Product updated successfully');
      } else {
        await addDoc(collection(db, 'super_products'), {
          ...newProduct,
          createdAt: serverTimestamp()
        });
        notify.confirm('Product added successfully');
      }
      setNewProduct({
        name: '',
        description: '',
        type: 'Product',
        packages: [{ name: '', price: 0, description: '', modules: [] }]
      });
      setIsAddingProduct(false);
      setEditingProduct(null);
    } catch (err) {
      handleFirestoreError(err, OperationType.CREATE, 'super_products');
      notify.error('Failed to save product');
    }
  };

  const handleDeleteProduct = async (id: string) => {
    try {
      if (isLocalSession()) {
        await localAdminRequest(`/api/admin/products/${id}`, { method: 'DELETE' });
        setProducts(current => current.filter(product => product.id !== id));
        notify.confirm('Product deleted');
        return;
      }
      await deleteDoc(doc(db, 'super_products', id));
      notify.confirm('Product deleted');
    } catch (err) {
      handleFirestoreError(err, OperationType.DELETE, `super_products/${id}`);
      notify.error('Failed to delete product');
    }
  };

  const handleDeleteUser = async (id: string) => {
    try {
      if (isLocalSession()) {
        await localAdminRequest(`/api/admin/users/${id}`, { method: 'DELETE' });
        setOrgUsers(current => current.filter(user => user.id !== id));
        notify.confirm('User removed');
        return;
      }
      await deleteDoc(doc(db, 'org_users', id));
      notify.confirm('User removed');
    } catch (err) {
      handleFirestoreError(err, OperationType.DELETE, `org_users/${id}`);
      notify.error('Failed to remove user');
    }
  };

  const handleAssignProduct = async (orgId: string, productId: string) => {
    if (!orgId) return;
    try {
      // Find the org to get current subscription data
      const org = organizations.find(o => o.id === orgId);
      if (!org) return;

      const subscription = { ...(org.subscription || {}), productId, packageId: '' };
      if (isLocalSession()) {
        const updated = await localAdminRequest<Organization>(`/api/admin/organizations/${orgId}`, { method: 'PATCH', body: JSON.stringify({ subscription }) });
        setOrganizations(current => current.map(entry => entry.id === updated.id ? updated : entry));
      } else {
        await updateDoc(doc(db, 'organizations', orgId), { subscription });
      }
      notify.confirm('Product assigned to organization');
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, `organizations/${orgId}`);
      notify.error('Failed to assign product');
    }
  };

  const handleUnassignProduct = async (orgId: string) => {
    try {
      if (isLocalSession()) {
        const organization = organizations.find(entry => entry.id === orgId);
        const updated = await localAdminRequest<Organization>(`/api/admin/organizations/${orgId}`, {
          method: 'PATCH', body: JSON.stringify({ subscription: { ...(organization?.subscription || {}), productId: '', packageId: '' } }),
        });
        setOrganizations(current => current.map(entry => entry.id === updated.id ? updated : entry));
      } else {
        await updateDoc(doc(db, 'organizations', orgId), {
          'subscription.productId': '',
          'subscription.packageId': ''
        });
      }
      notify.confirm('Product unassigned from organization');
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, `organizations/${orgId}`);
      notify.error('Failed to unassign product');
    }
  };

  const handleSaveAiSettings = async () => {
    if (!aiSettings.model.trim() || !aiSettings.baseUrl.trim()) {
      notify.error('Provider URL and model name are required');
      return;
    }
    setIsSavingAiSettings(true);
    try {
      const payload = await localAdminRequest<any>('/api/admin/ai-settings', {
        method: 'PUT',
        body: JSON.stringify({
          provider: aiSettings.provider,
          baseUrl: aiSettings.baseUrl,
          model: aiSettings.model,
          enabled: aiSettings.enabled,
          ...(aiSettings.apiKey.trim() ? { apiKey: aiSettings.apiKey.trim() } : {}),
        }),
      });
      setAiSettings(current => ({
        ...current,
        provider: payload.provider || current.provider,
        baseUrl: payload.baseUrl || current.baseUrl,
        model: payload.model || current.model,
        apiKey: '',
        apiKeyLast4: payload.apiKeyLast4 || current.apiKeyLast4,
        enabled: Boolean(payload.enabled),
      }));
      notify.confirm('AI provider settings saved');
    } catch (error: any) {
      notify.error(error?.message || 'Failed to save AI settings');
    } finally {
      setIsSavingAiSettings(false);
    }
  };

  const handleDownloadInstaller = (license: any, platform: 'Android' | 'Windows' | 'iOS') => {
    const org = organizations.find(o => o.id === license.orgId);
    
    const data = {
      licenseKey: license.key,
      org: org || null,
      permissions: license.featurePermissions,
      expiryDate: license.expiryDate,
      platform,
      manifest: {
        version: "1.2.0",
        platform,
        generatedAt: new Date().toISOString(),
        offlineToken: btoa(JSON.stringify({ 
          k: license.key, 
          o: license.orgId, 
          p: platform,
          t: Date.now() 
        }))
      }
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const extension = platform === 'Android' ? 'apk.bundle' : platform === 'Windows' ? 'exe.bundle' : 'ipa.bundle';
    a.download = `offline_${license.orgId || 'org'}_${platform}_${license.key.split('-')[0]}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    notify.confirm(`${platform} Offline Installer Downloaded!`);
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-8 h-8 text-primary" />
            Super Admin Panel
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage client organizations, users, and product offerings.
          </p>
        </div>
      </div>

      {/* Sub-module Navigation */}
      <div className="flex gap-4 border-b pb-4 overflow-x-auto whitespace-nowrap">
        <button
          onClick={() => { setActiveSubModule('organizations'); setSearchTerm(''); }}
          className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
            activeSubModule === 'organizations' ? 'bg-primary text-primary-foreground shadow-lg' : 'hover:bg-muted'
          }`}
        >
          Organizations
        </button>
        <button
          onClick={() => { setActiveSubModule('users'); setSearchTerm(''); }}
          className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
            activeSubModule === 'users' ? 'bg-primary text-primary-foreground shadow-lg' : 'hover:bg-muted'
          }`}
        >
          Users
        </button>
        <button
          onClick={() => { setActiveSubModule('products'); setSearchTerm(''); }}
          className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
            activeSubModule === 'products' ? 'bg-primary text-primary-foreground shadow-lg' : 'hover:bg-muted'
          }`}
        >
          Product & Service
        </button>
        <button
          onClick={() => { setActiveSubModule('licenses'); setSearchTerm(''); }}
          className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
            activeSubModule === 'licenses' ? 'bg-primary text-primary-foreground shadow-lg' : 'hover:bg-muted'
          }`}
        >
          License Keys
        </button>
        <button
          onClick={() => { setActiveSubModule('ai_settings'); setSearchTerm(''); }}
          className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
            activeSubModule === 'ai_settings' ? 'bg-primary text-primary-foreground shadow-lg' : 'hover:bg-muted'
          }`}
        >
          AI Settings
        </button>
      </div>

      {activeSubModule !== 'ai_settings' && <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input 
          type="text"
          placeholder={`Search ${activeSubModule}...`}
          value={searchTerm || ''}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-11 pr-4 py-3 bg-muted/30 border border-border/50 rounded-2xl outline-none focus:ring-2 focus:ring-primary/20 transition-all font-medium"
        />
      </div>}

      {activeSubModule === 'ai_settings' && (
        <div className="max-w-3xl space-y-6">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-primary/10 p-3 text-primary"><Sparkles className="h-6 w-6" /></div>
            <div>
              <h2 className="text-xl font-bold">OpenAI-compatible AI provider</h2>
              <p className="text-sm text-muted-foreground">Configure the server-side provider used by the organization-scoped floating assistant.</p>
            </div>
          </div>

          {!isLocalSession() ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              AI provider settings are available when the PostgreSQL server session is active. Firebase-only legacy sessions cannot save these settings.
            </div>
          ) : isLoadingAiSettings ? (
            <div className="flex items-center gap-2 rounded-2xl border bg-muted/20 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading provider settings…</div>
          ) : (
            <div className="space-y-6 rounded-2xl border bg-card p-6 shadow-sm">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <label className="space-y-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Provider name</span>
                  <input value={aiSettings.provider} onChange={event => setAiSettings(current => ({ ...current, provider: event.target.value }))} placeholder="openai-compatible" className="w-full rounded-xl border bg-background px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary/20" />
                </label>
                <label className="space-y-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Model name</span>
                  <input value={aiSettings.model} onChange={event => setAiSettings(current => ({ ...current, model: event.target.value }))} placeholder="gpt-4o-mini" className="w-full rounded-xl border bg-background px-3 py-2.5 font-mono outline-none focus:ring-2 focus:ring-primary/20" />
                </label>
                <label className="space-y-2 md:col-span-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">OpenAI-compatible base URL</span>
                  <input type="url" value={aiSettings.baseUrl} onChange={event => setAiSettings(current => ({ ...current, baseUrl: event.target.value }))} placeholder="https://api.openai.com/v1" className="w-full rounded-xl border bg-background px-3 py-2.5 font-mono text-sm outline-none focus:ring-2 focus:ring-primary/20" />
                  <span className="block text-xs text-muted-foreground">Use a free/self-hosted endpoint such as Ollama, LiteLLM, or another service exposing <code>/chat/completions</code>.</span>
                </label>
                <label className="space-y-2 md:col-span-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">API key <span className="font-normal normal-case">(optional for local providers)</span></span>
                  <input type="password" value={aiSettings.apiKey} onChange={event => setAiSettings(current => ({ ...current, apiKey: event.target.value }))} placeholder={aiSettings.apiKeyLast4 ? `Saved key ending in ••••${aiSettings.apiKeyLast4}; leave blank to keep it` : 'Enter provider key, if required'} autoComplete="new-password" className="w-full rounded-xl border bg-background px-3 py-2.5 font-mono outline-none focus:ring-2 focus:ring-primary/20" />
                  <span className="block text-xs text-muted-foreground">The key is encrypted and stored only on the server. It is never sent back to the browser.</span>
                </label>
              </div>
              <label className="flex cursor-pointer items-center gap-3 rounded-xl border bg-muted/20 p-4">
                <input type="checkbox" checked={aiSettings.enabled} onChange={event => setAiSettings(current => ({ ...current, enabled: event.target.checked }))} className="h-4 w-4 rounded text-primary focus:ring-primary/20" />
                <span><span className="block text-sm font-bold">Enable organization AI</span><span className="block text-xs text-muted-foreground">Users can ask questions only about the organization selected in their switcher.</span></span>
              </label>
              <div className="flex justify-end border-t pt-5">
                <button type="button" onClick={() => void handleSaveAiSettings()} disabled={isSavingAiSettings} className="flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50">
                  {isSavingAiSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save AI settings
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeSubModule === 'licenses' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Key className="w-6 h-6" />
              License Key Management
            </h2>
            
            <div className="flex-1 flex justify-center items-center gap-4">
              <div className="flex items-center gap-2 bg-muted/40 p-1 rounded-2xl border border-border/50">
                <span className="text-[10px] font-black uppercase tracking-widest px-3 opacity-40">System Installers</span>
                <button 
                  onClick={() => handleDownloadInstaller({ key: 'GENERIC', featurePermissions: ['all'] }, 'Windows')}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-[10px] font-bold uppercase transition-all hover:bg-white hover:shadow-sm"
                >
                  <Laptop className="w-3.5 h-3.5" /> Windows
                </button>
                <button 
                  onClick={() => handleDownloadInstaller({ key: 'GENERIC', featurePermissions: ['all'] }, 'Android')}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-[10px] font-bold uppercase transition-all hover:bg-white hover:shadow-sm"
                >
                  <Laptop className="w-3.5 h-3.5" /> Android
                </button>
                <button 
                  onClick={() => handleDownloadInstaller({ key: 'GENERIC', featurePermissions: ['all'] }, 'iOS')}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-[10px] font-bold uppercase transition-all hover:bg-white hover:shadow-sm"
                >
                  <Laptop className="w-3.5 h-3.5" /> iOS
                </button>
              </div>
            </div>

            <button 
              onClick={() => {
                setEditingLicense(null);
                setNewLicense({ expiryDays: 365, deviceLimit: 1, permissions: ['all'], orgId: '', isOffline: false });
                setIsAddingLicense(true);
              }}
              className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-bold uppercase transition-all hover:scale-105"
            >
              <Plus className="w-4 h-4" />
              Generate License
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {licenses
              .filter(l => {
                const searchLower = searchTerm.toLowerCase();
                const orgName = organizations.find(o => o.id === l.orgId)?.name || '';
                return l.key.toLowerCase().includes(searchLower) || 
                       orgName.toLowerCase().includes(searchLower);
              })
              .map((license) => (
              <div key={license.key} className="p-6 border rounded-[32px] bg-card shadow-sm hover:shadow-md transition-all flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <code className="text-lg font-bold font-mono tracking-wider bg-muted px-4 py-1 rounded-xl text-primary select-all">
                      {license.key}
                    </code>
                    <span className={`px-3 py-1 text-[10px] font-bold uppercase rounded-full ${
                      license.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {license.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-6 mt-2 flex-wrap text-xs text-muted-foreground font-medium">
                    <span className="flex items-center gap-1.5 bg-background px-3 py-1.5 rounded-full border shadow-sm"><History className="w-3.5 h-3.5" /> Expires: {new Date(license.expiryDate).toLocaleDateString()}</span>
                    <span className="flex items-center gap-1.5 bg-background px-3 py-1.5 rounded-full border shadow-sm"><Laptop className="w-3.5 h-3.5" /> Devices: {license.devices?.length || 0} / {license.deviceLimit}</span>
                    <span className="flex items-center gap-1.5 bg-primary/5 text-primary px-3 py-1.5 rounded-full border border-primary/20 shadow-sm"><ShieldCheck className="w-3.5 h-3.5" /> {license.featurePermissions?.includes('all') ? 'Full Access' : (license.featurePermissions?.length || 0) + ' Modules'}</span>
                    {license.orgId && (
                      <span className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full border border-blue-200 shadow-sm">
                        <Building2 className="w-3.5 h-3.5" /> {organizations.find(o => o.id === license.orgId)?.name || 'Unknown Org'}
                      </span>
                    )}
                  </div>
                  {license.featurePermissions && !license.featurePermissions.includes('all') && license.featurePermissions.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {license.featurePermissions.map((perm: string) => {
                        let mod = MODULES.find(m => m.id === perm);
                        if (!mod) {
                          for (const m of MODULES) {
                            const sm = m.subModules?.find((sub: any) => sub.id === perm);
                            if (sm) {
                              mod = sm as any;
                              break;
                            }
                          }
                        }
                        return (
                          <span key={perm} className="text-[10px] px-2 py-0.5 bg-secondary text-secondary-foreground rounded-full border uppercase font-bold tracking-wider">
                            {mod ? mod.label : perm}
                          </span>
                        )
                      })}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2 flex-wrap md:flex-nowrap">
                  <button 
                    onClick={() => handleToggleLicenseStatus(license.key, license.status)}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest border transition-all shadow-sm",
                      license.status === 'Active' ? "border-amber-500/20 text-amber-600 hover:bg-amber-500 hover:text-white" : "border-green-500/20 text-green-600 hover:bg-green-500 hover:text-white"
                    )}
                  >
                    {license.status === 'Active' ? 'Suspend' : 'Activate'}
                  </button>
                  
                  {license.isOffline && (
                    <div className="flex gap-2">
                       <button 
                        onClick={() => handleDownloadInstaller(license, 'Android')}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest border border-primary/20 text-blue-600 hover:bg-blue-50 transition-all shadow-sm"
                        title="Download Android Installer (APK)"
                      >
                        <Laptop className="w-3.5 h-3.5" />
                        APK
                      </button>
                      <button 
                        onClick={() => handleDownloadInstaller(license, 'Windows')}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest border border-primary/20 text-blue-600 hover:bg-blue-50 transition-all shadow-sm"
                        title="Download Windows Installer (EXE)"
                      >
                        <Laptop className="w-3.5 h-3.5" />
                        Win
                      </button>
                      <button 
                        onClick={() => handleDownloadInstaller(license, 'iOS')}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest border border-primary/20 text-blue-600 hover:bg-blue-50 transition-all shadow-sm"
                        title="Download iOS Installer (IPA)"
                      >
                        <Laptop className="w-3.5 h-3.5" />
                        iOS
                      </button>
                    </div>
                  )}

                  <button 
                    onClick={() => {
                      setEditingLicense(license);
                      setNewLicense({
                        key: license.key,
                        expiryDays: 0, 
                        deviceLimit: license.deviceLimit || 1,
                        permissions: license.featurePermissions || ['all'],
                        orgId: license.orgId || '',
                        isOffline: license.isOffline || false
                      });
                      setIsAddingLicense(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest border border-primary/20 text-primary hover:bg-primary hover:text-white transition-all shadow-sm"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => setViewingActivityLicense(license)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest border border-primary/20 text-primary hover:bg-primary/5 transition-all shadow-sm"
                  >
                    <Activity className="w-4 h-4" />
                    Log
                  </button>
                  <button 
                    onClick={() => setLicenseToReset(license.key)}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest border border-destructive/20 text-destructive hover:bg-destructive hover:text-white transition-all shadow-sm"
                  >
                    Reset Devices
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Generate License Modal */}
          {isAddingLicense && (
            <div
              className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center overflow-y-auto overscroll-contain p-4"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  setIsAddingLicense(false);
                  setEditingLicense(null);
                }
              }}
            >
              <div className="bg-card border border-black p-8 rounded-[40px] shadow-2xl w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain animate-in zoom-in-95 duration-200">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-2xl font-bold italic serif">{editingLicense ? 'Edit Key' : 'Generate Key'}</h3>
                  <button onClick={() => { setIsAddingLicense(false); setEditingLicense(null); }}><X /></button>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-center gap-4 bg-primary/5 p-4 rounded-[20px] border border-primary/10">
                    <div className="p-3 bg-primary/10 rounded-xl">
                      <Activity className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-bold">Offline Version Support</h4>
                      <p className="text-xs text-muted-foreground">Allow this license to be used in the offline APK with encrypted data.</p>
                    </div>
                    <button 
                      onClick={() => setNewLicense({ ...newLicense, isOffline: !newLicense.isOffline })}
                      className={cn(
                        "px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all",
                        newLicense.isOffline ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                      )}
                    >
                      {newLicense.isOffline ? 'Enabled' : 'Disabled'}
                    </button>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-50 ml-4">Assign to Organization</label>
                    <select 
                      value={newLicense.orgId || ''}
                      onChange={(e) => setNewLicense({ ...newLicense, orgId: e.target.value })}
                      className="w-full bg-muted/50 p-4 rounded-[20px] outline-none border border-transparent focus:border-primary transition-all text-sm"
                    >
                      <option value="">No Organization Assigned</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                  </div>

                  {!editingLicense && (
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest opacity-50 ml-4">Duration (Days)</label>
                      <input 
                        type="number"
                        value={newLicense.expiryDays ?? 0}
                        onChange={(e) => setNewLicense({ ...newLicense, expiryDays: parseInt(e.target.value) || 0 })}
                        className="w-full bg-muted/50 p-4 rounded-[20px] outline-none border border-transparent focus:border-primary transition-all"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-50 ml-4">Device Limit</label>
                    <input 
                      type="number"
                      value={newLicense.deviceLimit ?? 1}
                      onChange={(e) => setNewLicense({ ...newLicense, deviceLimit: parseInt(e.target.value) || 1 })}
                      className="w-full bg-muted/50 p-4 rounded-[20px] outline-none border border-transparent focus:border-primary transition-all"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between ml-4">
                      <label className="text-[10px] font-bold uppercase tracking-widest opacity-50">Permissions (Modules)</label>
                      <button 
                        onClick={() => {
                          if (newLicense.permissions.includes('all')) {
                            setNewLicense({ ...newLicense, permissions: [] });
                          } else {
                            setNewLicense({ ...newLicense, permissions: ['all'] });
                          }
                        }}
                        className="text-[10px] font-bold uppercase tracking-widest text-primary hover:underline"
                      >
                        {newLicense.permissions.includes('all') ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-muted/50 p-4 rounded-[20px] max-h-80 overflow-y-auto custom-scrollbar">
                      {MODULES.filter(m => m.id !== 'super_admin').map((mod) => (
                        <div key={mod.id} className="space-y-1">
                          <label className="flex items-center gap-2 cursor-pointer p-2 hover:bg-background rounded-xl transition-all">
                            <input 
                              type="checkbox"
                              checked={newLicense.permissions.includes('all') || newLicense.permissions.includes(mod.id)}
                              onChange={(e) => {
                                if (newLicense.permissions.includes('all')) {
                                  const allIds: string[] = [];
                                  MODULES.filter(m => m.id !== 'super_admin').forEach(m => {
                                    allIds.push(m.id);
                                    m.subModules?.forEach((sm: any) => allIds.push(sm.id));
                                  });
                                  setNewLicense({ ...newLicense, permissions: allIds.filter(id => id !== mod.id) });
                                } else {
                                  let newPerms = e.target.checked 
                                    ? [...newLicense.permissions, mod.id] 
                                    : newLicense.permissions.filter(p => p !== mod.id);
                                  
                                  // If unchecking main module, optionally uncheck submodules
                                  if (!e.target.checked && mod.subModules) {
                                      const subIds = mod.subModules.map((sm: any) => sm.id);
                                      newPerms = newPerms.filter(p => !subIds.includes(p));
                                  }

                                  setNewLicense({ ...newLicense, permissions: newPerms });
                                }
                              }}
                              className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4"
                            />
                            <span className="text-sm font-bold">{mod.label}</span>
                          </label>
                          
                          {mod.subModules && mod.subModules.length > 0 && (
                            <div className="ml-6 space-y-1 border-l border-primary/10 pl-2">
                              {mod.subModules.map((sm: any) => (
                                <label key={sm.id} className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-background rounded-lg transition-all text-muted-foreground hover:text-foreground">
                                  <input 
                                    type="checkbox"
                                    checked={newLicense.permissions.includes('all') || newLicense.permissions.includes(sm.id)}
                                    onChange={(e) => {
                                      if (newLicense.permissions.includes('all')) {
                                        const allIds: string[] = [];
                                        MODULES.filter(m => m.id !== 'super_admin').forEach(m => {
                                          allIds.push(m.id);
                                          m.subModules?.forEach((sub: any) => allIds.push(sub.id));
                                        });
                                        setNewLicense({ ...newLicense, permissions: allIds.filter(id => id !== sm.id) });
                                      } else {
                                        let newPerms = e.target.checked 
                                          ? [...newLicense.permissions, sm.id] 
                                          : newLicense.permissions.filter(p => p !== sm.id);
                                        
                                        // If checking a submodule, ensure the parent is also checked
                                        if (e.target.checked && !newPerms.includes(mod.id)) {
                                          newPerms.push(mod.id);
                                        }

                                        setNewLicense({ ...newLicense, permissions: newPerms });
                                      }
                                    }}
                                    className="rounded border-gray-300 text-primary/70 focus:ring-primary h-3.5 w-3.5"
                                  />
                                  <span className="text-xs">{sm.label}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <button 
                    onClick={handleGenerateLicense}
                    className="w-full bg-primary text-primary-foreground py-4 rounded-[20px] font-bold shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all"
                  >
                    {editingLicense ? 'Save Changes' : 'Confirm & Generate'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeSubModule === 'organizations' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Building2 className="w-6 h-6" />
              Organizations Management
            </h2>
            <button 
              onClick={() => {
                setIsAddingOrg(true);
                setEditingOrg(null);
                setNewOrg({ 
                  name: '', 
                  country: '', 
                  city: '',
                  industry: '', 
                  targetAudience: '', 
                  status: 'Active',
                  assignedModules: [],
                  licenseKey: '',
                  location: { lat: 25.2048, lng: 55.2708, address: '' },
                  subscription: { productId: '', packageId: '', purchaseDate: '', warningDate: '', renewalDate: '' }
                });
              }}
              className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-bold uppercase"
            >
              <Plus className="w-4 h-4" />
              Add Organization
            </button>
          </div>

          {isAddingOrg && (
            <div
              className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center overflow-y-auto overscroll-contain p-4"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  setIsAddingOrg(false);
                  setEditingOrg(null);
                }
              }}
            >
              <div className="bg-card border rounded-3xl shadow-2xl w-full max-w-4xl max-h-[calc(100dvh-2rem)] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
                <div className="p-6 border-b flex items-center justify-between bg-muted/30">
                  <div>
                    <h3 className="text-xl font-bold">{editingOrg ? 'Edit Organization' : 'New Organization'}</h3>
                    <p className="text-sm text-muted-foreground">Configure organization details and module access</p>
                  </div>
                  <button 
                    onClick={() => {
                      setIsAddingOrg(false);
                      setEditingOrg(null);
                    }}
                    className="p-2 hover:bg-muted rounded-full transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-8 space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Organization Name</label>
                      <input 
                        type="text"
                        value={newOrg.name || ''}
                        onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                        placeholder="Accountex Ltd"
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Country</label>
                      <select 
                        value={newOrg.country || ''}
                        onChange={(e) => setNewOrg({ ...newOrg, country: e.target.value, city: '' })}
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                      >
                        <option value="">Select Country...</option>
                        {COUNTRIES.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">City</label>
                      {newOrg.country && CITY_MAP[newOrg.country] ? (
                        <select
                          value={newOrg.city || ''}
                          onChange={(e) => setNewOrg({ ...newOrg, city: e.target.value })}
                          className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                        >
                          <option value="">Select City...</option>
                          {CITY_MAP[newOrg.country].map(city => (
                            <option key={city} value={city}>{city}</option>
                          ))}
                        </select>
                      ) : (
                        <input 
                          type="text"
                          value={newOrg.city || ''}
                          onChange={(e) => setNewOrg({ ...newOrg, city: e.target.value })}
                          placeholder="Enter City"
                          className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                        />
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Industry</label>
                      <input 
                        type="text"
                        value={newOrg.industry || ''}
                        onChange={(e) => setNewOrg({ ...newOrg, industry: e.target.value })}
                        placeholder="Fintech"
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Target Audience</label>
                      <input 
                        type="text"
                        value={newOrg.targetAudience || ''}
                        onChange={(e) => setNewOrg({ ...newOrg, targetAudience: e.target.value })}
                        placeholder="SMEs"
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Status</label>
                      <select 
                        value={newOrg.status || 'Active'}
                        onChange={(e) => setNewOrg({ ...newOrg, status: e.target.value as any })}
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Suspended">Suspended</option>
                        <option value="On Hold">On Hold</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Location</label>
                      <div className="flex gap-2">
                        <input 
                          type="text"
                          readOnly
                          value={newOrg.location?.address || `${newOrg.location?.lat.toFixed(4)}, ${newOrg.location?.lng.toFixed(4)}`}
                          onClick={() => setIsMapModalOpen(true)}
                          placeholder="Click to pick location"
                          className="flex-1 px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all cursor-pointer"
                        />
                        <button 
                          onClick={() => setIsMapModalOpen(true)}
                          className="p-2.5 bg-primary/10 text-primary rounded-xl hover:bg-primary/20 transition-colors"
                        >
                          <MapPin className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Subscription Product</label>
                      <select 
                        value={newOrg.subscription?.productId || ''}
                        onChange={(e) => setNewOrg({ 
                          ...newOrg, 
                          subscription: { 
                            ...newOrg.subscription!, 
                            productId: e.target.value,
                            packageId: '' // Reset package when product changes
                          } 
                        })}
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                      >
                        <option value="">Select a Product/Service</option>
                        {products.map(p => (
                          <option key={p.id} value={p.id}>{p.name} ({p.type})</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Subscription Package</label>
                      <select 
                        value={newOrg.subscription?.packageId || ''}
                        onChange={(e) => handlePackageChange(e.target.value)}
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                        disabled={!newOrg.subscription?.productId}
                      >
                        <option value="">Select a Package</option>
                        {products
                          .find(p => p.id === newOrg.subscription?.productId)
                          ?.packages.map(pkg => (
                            <option key={pkg.name} value={pkg.name}>{pkg.name} - ${pkg.price}</option>
                          ))
                        }
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Purchase Date</label>
                      <input 
                        type="date"
                        value={newOrg.subscription?.purchaseDate || ''}
                        onChange={(e) => setNewOrg({ ...newOrg, subscription: { ...newOrg.subscription!, purchaseDate: e.target.value } })}
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Warning Date</label>
                      <input 
                        type="date"
                        value={newOrg.subscription?.warningDate || ''}
                        onChange={(e) => setNewOrg({ ...newOrg, subscription: { ...newOrg.subscription!, warningDate: e.target.value } })}
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Renewal Date</label>
                      <input 
                        type="date"
                        value={newOrg.subscription?.renewalDate || ''}
                        onChange={(e) => setNewOrg({ ...newOrg, subscription: { ...newOrg.subscription!, renewalDate: e.target.value } })}
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase opacity-50">Linked License Key</label>
                    <select 
                      value={newOrg.licenseKey || ''}
                      onChange={(e) => setNewOrg({ ...newOrg, licenseKey: e.target.value })}
                      className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                    >
                      <option value="">No License Linked</option>
                      {licenses.map(l => (
                        <option key={l.key} value={l.key}>{l.key} ({l.status})</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-4">
                    <label className="text-xs font-bold uppercase opacity-50">Assigned Modules & Sub-modules</label>
                    <div className="space-y-4">
                      {MODULES.map(module => (
                        <div key={module.id} className="space-y-2">
                          <label className="flex items-center gap-2 font-bold text-sm">
                            <input
                              type="checkbox"
                              checked={newOrg.assignedModules?.includes(module.id)}
                              onChange={(e) => {
                                const modules = newOrg.assignedModules || [];
                                if (e.target.checked) {
                                  // Add module and all sub-modules
                                  const subModuleIds = module.subModules.map(sm => sm.id);
                                  setNewOrg({ ...newOrg, assignedModules: Array.from(new Set([...modules, module.id, ...subModuleIds])) });
                                } else {
                                  // Remove module and all sub-modules
                                  const subModuleIds = module.subModules.map(sm => sm.id);
                                  setNewOrg({ ...newOrg, assignedModules: modules.filter(id => id !== module.id && !subModuleIds.includes(id)) });
                                }
                              }}
                            />
                            {module.label}
                          </label>
                          {module.subModules.length > 0 && (
                            <div className="grid grid-cols-2 gap-2 pl-6">
                              {module.subModules.map(subModule => (
                                <label key={subModule.id} className="flex items-center gap-2 text-xs">
                                  <input
                                    type="checkbox"
                                    checked={newOrg.assignedModules?.includes(subModule.id)}
                                    onChange={(e) => {
                                      const modules = newOrg.assignedModules || [];
                                      if (e.target.checked) {
                                        setNewOrg({ ...newOrg, assignedModules: [...modules, subModule.id] });
                                      } else {
                                        setNewOrg({ ...newOrg, assignedModules: modules.filter(id => id !== subModule.id) });
                                      }
                                    }}
                                  />
                                  {subModule.label}
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t bg-muted/30 flex justify-end gap-4">
                  <button 
                    onClick={() => {
                      setIsAddingOrg(false);
                      setEditingOrg(null);
                    }}
                    className="px-6 py-2.5 rounded-xl font-bold uppercase text-xs hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={editingOrg ? handleUpdateOrg : handleAddOrg}
                    className="bg-primary text-primary-foreground px-8 py-2.5 rounded-xl font-bold uppercase text-xs shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all"
                  >
                    {editingOrg ? 'Update Organization' : 'Save Organization'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4">
            {organizations
              .filter(org => {
                const searchLower = searchTerm.toLowerCase();
                return org.name.toLowerCase().includes(searchLower) || 
                       org.industry.toLowerCase().includes(searchLower) ||
                       (org.city || '').toLowerCase().includes(searchLower) ||
                       org.country.toLowerCase().includes(searchLower);
              })
              .map(org => (
              <div key={org.id} className="group p-4 border rounded-2xl bg-card hover:border-primary/50 hover:shadow-lg transition-all flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-primary/10 text-primary rounded-xl">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <button 
                      onClick={() => setSelectedOrgProfile(org)}
                      className="text-lg font-bold hover:text-primary transition-colors text-left"
                    >
                      {org.name}
                    </button>
                    <p className="text-xs text-muted-foreground">{org.industry} • {org.city ? `${org.city}, ` : ''}{org.country}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className={`px-3 py-1 text-[10px] font-bold uppercase rounded-lg ${
                    org.status === 'Active' ? 'bg-green-500/10 text-green-600' : 
                    org.status === 'Suspended' ? 'bg-red-500/10 text-red-600' : 
                    org.status === 'On Hold' ? 'bg-amber-500/10 text-amber-600' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {org.status}
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => {
                        setEditingOrg(org);
                        setNewOrg({
                          name: org.name || '',
                          country: org.country || '',
                          city: org.city || '',
                          industry: org.industry || '',
                          targetAudience: org.targetAudience || '',
                          status: (org.status as any) || 'Active',
                          assignedModules: org.assignedModules || [],
                          licenseKey: org.licenseKey || '',
                          location: org.location || { lat: 25.2048, lng: 55.2708, address: '' },
                          subscription: {
                            productId: org.subscription?.productId || '',
                            packageId: org.subscription?.packageId || '',
                            purchaseDate: org.subscription?.purchaseDate || '',
                            warningDate: org.subscription?.warningDate || '',
                            renewalDate: org.subscription?.renewalDate || ''
                          }
                        });
                        setIsAddingOrg(true);
                      }}
                      className="p-2 hover:bg-primary/10 text-primary rounded-xl transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDeleteOrg(org.id)}
                      className="p-2 hover:bg-destructive/10 text-destructive rounded-xl transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Map Picker Modal */}
      {isMapModalOpen && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsMapModalOpen(false);
          }}
        >
          <div className="bg-card border rounded-3xl shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col overflow-hidden">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold">Pick Location</h3>
              <button onClick={() => setIsMapModalOpen(false)} className="p-2 hover:bg-muted rounded-full">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 relative">
              {((import.meta as any).env).VITE_GOOGLE_MAPS_API_KEY ? (
                <GoogleMapReact
                  bootstrapURLKeys={{ key: ((import.meta as any).env).VITE_GOOGLE_MAPS_API_KEY }}
                  defaultCenter={{ lat: newOrg.location?.lat || 25.2048, lng: newOrg.location?.lng || 55.2708 }}
                  defaultZoom={11}
                  onClick={(e) => {
                    setNewOrg({
                      ...newOrg,
                      location: {
                        lat: e.lat,
                        lng: e.lng,
                        address: `${e.lat.toFixed(6)}, ${e.lng.toFixed(6)}`
                      }
                    });
                  }}
                >
                  <div
                    // @ts-ignore
                    lat={newOrg.location?.lat}
                    // @ts-ignore
                    lng={newOrg.location?.lng}
                    className="relative -translate-x-1/2 -translate-y-full"
                  >
                    <MapPin className="w-8 h-8 text-destructive fill-destructive/20" />
                  </div>
                </GoogleMapReact>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-muted/30 p-8 text-center">
                  <MapPin className="w-12 h-12 text-muted-foreground mb-4 opacity-20" />
                  <p className="text-lg font-bold">Google Maps API Key Missing</p>
                  <p className="text-sm text-muted-foreground max-w-md mt-2">
                    Please configure <code className="bg-muted px-1 rounded">VITE_GOOGLE_MAPS_API_KEY</code> in your environment settings to enable the map picker.
                  </p>
                  <div className="mt-6 p-4 border rounded-xl bg-background text-left w-full max-w-sm">
                    <p className="text-xs font-bold uppercase opacity-50 mb-2">Simulated Coordinates</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] uppercase opacity-50">Latitude</label>
                        <input 
                          type="number" 
                          value={isNaN(newOrg.location?.lat || 0) ? '' : newOrg.location?.lat}
                          onChange={(e) => setNewOrg({ ...newOrg, location: { ...newOrg.location!, lat: e.target.value === '' ? 0 : parseFloat(e.target.value) } })}
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase opacity-50">Longitude</label>
                        <input 
                          type="number" 
                          value={isNaN(newOrg.location?.lng || 0) ? '' : newOrg.location?.lng}
                          onChange={(e) => setNewOrg({ ...newOrg, location: { ...newOrg.location!, lng: e.target.value === '' ? 0 : parseFloat(e.target.value) } })}
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t bg-muted/30 flex justify-between items-center">
              <p className="text-sm text-muted-foreground italic">
                Click on the map to place the pin
              </p>
              <button 
                onClick={() => setIsMapModalOpen(false)}
                className="bg-primary text-primary-foreground px-8 py-2.5 rounded-xl font-bold uppercase text-xs"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Organization Profile Modal */}
      {selectedOrgProfile && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSelectedOrgProfile(null);
          }}
        >
          <div className="bg-card border rounded-3xl shadow-2xl w-full max-w-5xl max-h-[calc(100dvh-2rem)] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b flex items-center justify-between bg-muted/30">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 text-primary rounded-2xl">
                  <Building2 className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold italic serif">{selectedOrgProfile.name}</h3>
                  <p className="text-sm text-muted-foreground">{selectedOrgProfile.industry} • {selectedOrgProfile.country}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                  <button 
                    onClick={() => {
                      setEditingOrg(selectedOrgProfile);
                      setNewOrg({
                        name: selectedOrgProfile.name || '',
                        country: selectedOrgProfile.country || '',
                        city: selectedOrgProfile.city || '',
                        industry: selectedOrgProfile.industry || '',
                        targetAudience: selectedOrgProfile.targetAudience || '',
                        status: (selectedOrgProfile.status as any) || 'Active',
                        assignedModules: selectedOrgProfile.assignedModules || [],
                        licenseKey: selectedOrgProfile.licenseKey || '',
                        location: selectedOrgProfile.location || { lat: 25.2048, lng: 55.2708, address: '' },
                        subscription: {
                          productId: selectedOrgProfile.subscription?.productId || '',
                          packageId: selectedOrgProfile.subscription?.packageId || '',
                          purchaseDate: selectedOrgProfile.subscription?.purchaseDate || '',
                          warningDate: selectedOrgProfile.subscription?.warningDate || '',
                          renewalDate: selectedOrgProfile.subscription?.renewalDate || ''
                        }
                      });
                      setIsEditingBillingDetails(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 hover:bg-primary/10 text-primary rounded-xl transition-colors text-xs font-bold uppercase"
                  >
                    <Wallet className="w-4 h-4" />
                    Edit Billing Details
                  </button>
                  <button 
                    onClick={() => {
                      setEditingOrg(selectedOrgProfile);
                      setNewOrg({
                        name: selectedOrgProfile.name || '',
                        country: selectedOrgProfile.country || '',
                        city: selectedOrgProfile.city || '',
                        industry: selectedOrgProfile.industry || '',
                        targetAudience: selectedOrgProfile.targetAudience || '',
                        status: (selectedOrgProfile.status as any) || 'Active',
                        assignedModules: selectedOrgProfile.assignedModules || [],
                        licenseKey: selectedOrgProfile.licenseKey || '',
                        location: selectedOrgProfile.location || { lat: 25.2048, lng: 55.2708, address: '' },
                        subscription: {
                          productId: selectedOrgProfile.subscription?.productId || '',
                          packageId: selectedOrgProfile.subscription?.packageId || '',
                          purchaseDate: selectedOrgProfile.subscription?.purchaseDate || '',
                          warningDate: selectedOrgProfile.subscription?.warningDate || '',
                          renewalDate: selectedOrgProfile.subscription?.renewalDate || ''
                        }
                      });
                      setIsEditingOrgDetails(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 hover:bg-primary/10 text-primary rounded-xl transition-colors text-xs font-bold uppercase"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit Details
                  </button>
                <button 
                  onClick={() => setSelectedOrgProfile(null)}
                  className="p-2 hover:bg-muted rounded-full transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-10">
              {/* Stats/Quick Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 rounded-2xl bg-muted/30 border border-border/50">
                  <p className="text-xs font-bold uppercase opacity-50 mb-1">Status</p>
                  <p className="text-lg font-bold">{selectedOrgProfile.status}</p>
                </div>
                <div className="p-6 rounded-2xl bg-muted/30 border border-border/50">
                  <p className="text-xs font-bold uppercase opacity-50 mb-1">Target Audience</p>
                  <p className="text-lg font-bold">{selectedOrgProfile.targetAudience}</p>
                </div>
                <div className="p-6 rounded-2xl bg-muted/30 border border-border/50">
                  <p className="text-xs font-bold uppercase opacity-50 mb-1">Total Users</p>
                  <p className="text-lg font-bold">
                    {orgUsers.filter(u => u.memberships?.some(m => m.orgId === selectedOrgProfile.id)).length}
                  </p>
                </div>
                <div className="p-6 rounded-2xl bg-primary/5 border border-primary/20 md:col-span-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase opacity-50 mb-1">License Details</p>
                      <div className="flex items-center gap-2 mt-2">
                        <ShieldCheck className="w-5 h-5 text-primary" />
                        <span className="text-lg font-bold">{selectedOrgProfile.licenseKey || 'No License Linked'}</span>
                      </div>
                    </div>
                    {selectedOrgProfile.licenseKey && (
                      <div className="text-right">
                        {licenses.find(l => l.key === selectedOrgProfile.licenseKey) && (
                          <>
                            <p className="text-[10px] font-bold uppercase opacity-50">Status: {licenses.find(l => l.key === selectedOrgProfile.licenseKey)?.status}</p>
                            <p className="text-[10px] font-bold uppercase opacity-50 mt-1">Expires: {new Date(licenses.find(l => l.key === selectedOrgProfile.licenseKey)?.expiryDate || '').toLocaleDateString()}</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {selectedOrgProfile.location && (
                  <div className="p-6 rounded-2xl bg-muted/30 border border-border/50 md:col-span-3">
                    <p className="text-xs font-bold uppercase opacity-50 mb-1">Location</p>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary" />
                      <p className="text-sm font-bold">{selectedOrgProfile.location.address || `${selectedOrgProfile.location.lat.toFixed(6)}, ${selectedOrgProfile.location.lng.toFixed(6)}`}</p>
                      <a 
                        href={`https://www.google.com/maps/search/?api=1&query=${selectedOrgProfile.location.lat},${selectedOrgProfile.location.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-auto text-[10px] font-bold uppercase text-primary hover:underline"
                      >
                        View on Google Maps
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Modules Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Assigned Modules
                  </h4>
                  <button 
                    onClick={() => {
                      setEditingOrg(selectedOrgProfile);
                      setNewOrg({
                        name: selectedOrgProfile.name || '',
                        country: selectedOrgProfile.country || '',
                        city: selectedOrgProfile.city || '',
                        industry: selectedOrgProfile.industry || '',
                        targetAudience: selectedOrgProfile.targetAudience || '',
                        status: (selectedOrgProfile.status as any) || 'Active',
                        assignedModules: selectedOrgProfile.assignedModules || [],
                        licenseKey: selectedOrgProfile.licenseKey || '',
                        location: selectedOrgProfile.location || { lat: 25.2048, lng: 55.2708, address: '' },
                        subscription: {
                          productId: selectedOrgProfile.subscription?.productId || '',
                          packageId: selectedOrgProfile.subscription?.packageId || '',
                          purchaseDate: selectedOrgProfile.subscription?.purchaseDate || '',
                          warningDate: selectedOrgProfile.subscription?.warningDate || '',
                          renewalDate: selectedOrgProfile.subscription?.renewalDate || ''
                        }
                      });
                      setIsEditingModules(true);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-primary/10 text-primary rounded-lg transition-colors text-[10px] font-bold uppercase"
                  >
                    <Edit2 className="w-3 h-3" />
                    Edit
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {MODULES.map(module => {
                    const isAssigned = selectedOrgProfile.assignedModules?.includes(module.id);
                    return (
                      <div 
                        key={module.id}
                        className={`p-4 rounded-2xl border transition-all flex flex-col items-center gap-3 text-center relative ${
                          isAssigned 
                            ? 'bg-primary/5 border-primary/20 text-primary' 
                            : 'opacity-40 grayscale bg-muted/50'
                        }`}
                      >
                        {isAssigned && (
                          <div className="absolute top-2 right-2 text-green-500">
                            <CheckCircle2 className="w-4 h-4" />
                          </div>
                        )}
                        <div className={`p-3 rounded-xl ${isAssigned ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                          {module.icon}
                        </div>
                        <span className="text-xs font-bold">{module.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Users Section */}
              <div className="space-y-4">
                <h4 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                  <UsersIcon className="w-4 h-4" />
                  Assigned Users
                </h4>
                <div className="border rounded-2xl overflow-hidden">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-4 text-xs font-bold uppercase opacity-50">User</th>
                        <th className="p-4 text-xs font-bold uppercase opacity-50">Role</th>
                        <th className="p-4 text-xs font-bold uppercase opacity-50 text-center">Key Person</th>
                        <th className="p-4 text-xs font-bold uppercase opacity-50 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {orgUsers
                        .filter(u => u.memberships?.some(m => m.orgId === selectedOrgProfile.id))
                        .map(user => {
                          const membership = user.memberships?.find(m => m.orgId === selectedOrgProfile.id);
                          const isKeyPerson = selectedOrgProfile.keyPersonId === user.id;
                          return (
                            <tr key={user.id} className="hover:bg-muted/30 transition-colors">
                              <td className="p-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">
                                    {(user.fullName || user.email || '?')[0].toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="text-sm font-bold">{user.fullName}</p>
                                    <p className="text-[10px] opacity-50">{user.email}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="p-4">
                                <span className="px-2 py-1 rounded bg-primary/10 text-[10px] font-bold uppercase text-primary">
                                  {membership?.role}
                                </span>
                              </td>
                              <td className="p-4 text-center">
                                <div className="flex justify-center">
                                  <input 
                                    type="checkbox" 
                                    className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer transition-all hover:scale-110"
                                    checked={isKeyPerson}
                                    onChange={async () => {
                                      try {
                                        await updateDoc(doc(db, 'organizations', selectedOrgProfile.id), {
                                          keyPersonId: isKeyPerson ? null : user.id
                                        });
                                        setSelectedOrgProfile(prev => prev ? { ...prev, keyPersonId: isKeyPerson ? undefined : user.id } : null);
                                        notify.confirm(isKeyPerson ? 'Key person removed' : 'Key person assigned');
                                      } catch (err) {
                                        console.error("Update failed:", err);
                                        notify.error('Failed to update key person. Please try again.');
                                      }
                                    }}
                                  />
                                </div>
                              </td>
                              <td className="p-4 text-right">
                                <button 
                                  onClick={() => {
                                    setEditingUser(user);
                                    setNewUser({
                                      fullName: user.fullName || '',
                                      email: user.email || '',
                                      phoneNumber: user.phoneNumber || '',
                                      nationality: user.nationality || '',
                                      countryCode: user.countryCode || '+20',
                                      gender: user.gender || 'male',
                                      password: '',
                                      memberships: user.memberships || []
                                    });
                                    setIsAddingUser(true);
                                  }}
                                  className="p-2 hover:bg-primary/10 text-primary rounded-lg transition-colors"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      {orgUsers.filter(u => u.memberships?.some(m => m.orgId === selectedOrgProfile.id)).length === 0 && (
                        <tr>
                          <td colSpan={3} className="p-8 text-center text-sm opacity-50 italic">
                            No users assigned to this organization yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Subscription Details Section */}
              {selectedOrgProfile.subscription && (
                <div className="space-y-4">
                  <h4 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4" />
                    Subscription Details
                  </h4>
                  <div className="p-6 border-2 border-dashed rounded-2xl relative overflow-hidden group bg-muted/10">
                    <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                      <ShieldCheck className="w-24 h-24 rotate-12" />
                    </div>
                    <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                      <div className="space-y-1">
                        <p className="text-[10px] font-black uppercase opacity-40 tracking-widest">Product / Service</p>
                        <p className="text-lg font-bold text-primary">
                          {products.find(p => p.id === selectedOrgProfile.subscription?.productId)?.name || 'N/A'}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-[10px] font-black uppercase opacity-40 tracking-widest">Active Package</p>
                        <p className="text-lg font-bold">{selectedOrgProfile.subscription?.packageId || 'Standard Plan'}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-[10px] font-black uppercase opacity-40 tracking-widest">Purchase Date</p>
                        <p className="text-lg font-bold">{selectedOrgProfile.subscription.purchaseDate || 'N/A'}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-[10px] font-black uppercase opacity-40 tracking-widest">Warning Date</p>
                        <div className="flex items-center gap-3">
                          <p className="text-lg font-bold text-amber-500">{selectedOrgProfile.subscription.warningDate || 'N/A'}</p>
                          <button 
                            onClick={() => {
                              const keyPerson = orgUsers.find(u => u.id === selectedOrgProfile.keyPersonId);
                              if (keyPerson) {
                                const subject = `Action Required: Your Accountex Subscription for ${selectedOrgProfile.name} is Expiring Soon`;
                                const body = `Dear ${keyPerson.fullName},\n\nWe hope you are enjoying your experience with Accountex.\n\nThis is a friendly reminder that your current subscription for ${selectedOrgProfile.name} is scheduled to expire on ${selectedOrgProfile.subscription?.warningDate || 'soon'}. To ensure uninterrupted access to all your business tools and data, we recommend renewing your package at your earliest convenience.\n\nPackage: ${selectedOrgProfile.subscription?.packageId || 'Standard Plan'}\nExpiry Date: ${selectedOrgProfile.subscription?.renewalDate || 'N/A'}\n\nIf you have any questions or need assistance with the renewal process, please don't hesitate to contact our support team.\n\nBest regards,\nThe Accountex Team`;
                                
                                setEmailDetails({
                                  to: keyPerson.email,
                                  cc: '',
                                  subject,
                                  body
                                });
                                setIsEmailComposerOpen(true);
                              } else {
                                notify.error("Please assign a Key Person first to send emails.");
                              }
                            }}
                            className={cn(
                              "p-2 rounded-xl transition-all shadow-sm border",
                              selectedOrgProfile.keyPersonId 
                                ? "bg-amber-500 text-white hover:bg-amber-600 border-amber-600" 
                                : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                            )}
                            title="Send Warning Email"
                          >
                            <Mail className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-[10px] font-black uppercase opacity-40 tracking-widest">Renewal Date</p>
                        <div className="flex items-center gap-3">
                          <p className="text-lg font-bold text-green-600">{selectedOrgProfile.subscription.renewalDate || 'N/A'}</p>
                          <button 
                            onClick={() => {
                              const keyPerson = orgUsers.find(u => u.id === selectedOrgProfile.keyPersonId);
                              if (keyPerson) {
                                const subject = `Success: Your Accountex Subscription for ${selectedOrgProfile.name} has been Renewed`;
                                const body = `Dear ${keyPerson.fullName},\n\nGreat news! Your subscription for ${selectedOrgProfile.name} has been successfully processed.\n\nYour new subscription period starts today, ${selectedOrgProfile.subscription?.purchaseDate || 'today'}. You can continue to enjoy full access to all the features included in your ${selectedOrgProfile.subscription?.packageId || 'Standard Plan'} plan.\n\nRenewal Date: ${selectedOrgProfile.subscription?.renewalDate || 'N/A'}\n\nThank you for your continued partnership with Accountex. We are excited to support your business growth.\n\nBest regards,\nThe Accountex Team`;
                                
                                setEmailDetails({
                                  to: keyPerson.email,
                                  cc: '',
                                  subject,
                                  body
                                });
                                setIsEmailComposerOpen(true);
                              } else {
                                notify.error("Please assign a Key Person first to send emails.");
                              }
                            }}
                            className={cn(
                              "p-2 rounded-xl transition-all shadow-sm border",
                              selectedOrgProfile.keyPersonId 
                                ? "bg-green-500 text-white hover:bg-green-600 border-green-600" 
                                : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                            )}
                            title="Send Renewal Email"
                          >
                            <Mail className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Composer Modal */}
      {isEmailComposerOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center overflow-y-auto overscroll-contain p-4 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsEmailComposerOpen(false);
          }}
        >
          <div className="bg-white border rounded-3xl shadow-2xl w-full max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b bg-muted/30 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Email Composer</h3>
                  <p className="text-xs opacity-50">Send subscription notification</p>
                </div>
              </div>
              <button onClick={() => setIsEmailComposerOpen(false)} className="p-2 hover:bg-muted rounded-full transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase opacity-40 tracking-widest ml-1">Recipient (To)</label>
                  <input 
                    type="email" 
                    value={emailDetails.to}
                    onChange={(e) => setEmailDetails(prev => ({ ...prev, to: e.target.value }))}
                    className="w-full p-3 bg-muted/50 border rounded-2xl text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                    placeholder="recipient@example.com"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase opacity-40 tracking-widest ml-1">Additional Emails (CC)</label>
                  <input 
                    type="text" 
                    value={emailDetails.cc}
                    onChange={(e) => setEmailDetails(prev => ({ ...prev, cc: e.target.value }))}
                    className="w-full p-3 bg-muted/50 border rounded-2xl text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                    placeholder="email1@example.com, email2@example.com"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase opacity-40 tracking-widest ml-1">Subject</label>
                <input 
                  type="text" 
                  value={emailDetails.subject}
                  onChange={(e) => setEmailDetails(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full p-3 bg-muted/50 border rounded-2xl text-sm font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                  placeholder="Email Subject"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase opacity-40 tracking-widest ml-1">Message Body</label>
                <textarea 
                  value={emailDetails.body}
                  onChange={(e) => setEmailDetails(prev => ({ ...prev, body: e.target.value }))}
                  className="w-full p-4 bg-muted/50 border rounded-2xl text-sm min-h-[250px] focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none"
                  placeholder="Write your message here..."
                />
              </div>
            </div>

            <div className="p-6 bg-muted/30 border-t flex items-center justify-end gap-3">
              <button 
                onClick={() => setIsEmailComposerOpen(false)}
                className="px-6 py-2.5 text-sm font-bold hover:bg-muted rounded-2xl transition-all"
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  const to = encodeURIComponent(emailDetails.to);
                  const cc = emailDetails.cc ? `&cc=${encodeURIComponent(emailDetails.cc)}` : '';
                  const subject = encodeURIComponent(emailDetails.subject);
                  const body = encodeURIComponent(emailDetails.body);
                  
                  const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${to}${cc}&su=${subject}&body=${body}`;
                  window.open(gmailUrl, '_blank');
                  
                  setIsEmailComposerOpen(false);
                  notify.confirm('Opening Gmail...');
                }}
                className="px-8 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-2xl shadow-lg shadow-blue-600/20 hover:bg-blue-700 hover:scale-105 active:scale-95 transition-all flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Send via Gmail
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Details Modal */}
      {isEditingOrgDetails && (
        <div className="fixed inset-0 bg-black/5 z-[60] flex items-center justify-center p-4">
          <div className="bg-white border rounded-3xl shadow-2xl w-full max-w-2xl animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold">Edit Organization Details</h3>
              <button onClick={() => setIsEditingOrgDetails(false)} className="p-2 hover:bg-muted rounded-full">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase opacity-50">Organization Name</label>
                  <input 
                    type="text"
                    value={newOrg.name}
                    onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                    className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase opacity-50">Country</label>
                  <input 
                    type="text"
                    value={newOrg.country}
                    onChange={(e) => setNewOrg({ ...newOrg, country: e.target.value })}
                    className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase opacity-50">Industry</label>
                  <input 
                    type="text"
                    value={newOrg.industry}
                    onChange={(e) => setNewOrg({ ...newOrg, industry: e.target.value })}
                    className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase opacity-50">Target Audience</label>
                  <input 
                    type="text"
                    value={newOrg.targetAudience}
                    onChange={(e) => setNewOrg({ ...newOrg, targetAudience: e.target.value })}
                    className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase opacity-50">Status</label>
                  <select 
                    value={newOrg.status}
                    onChange={(e) => setNewOrg({ ...newOrg, status: e.target.value as any })}
                    className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Suspended">Suspended</option>
                    <option value="On Hold">On Hold</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase opacity-50">Location</label>
                  <div className="flex gap-2">
                    <input 
                      type="text"
                      readOnly
                      value={newOrg.location?.address || `${newOrg.location?.lat.toFixed(4)}, ${newOrg.location?.lng.toFixed(4)}`}
                      onClick={() => setIsMapModalOpen(true)}
                      placeholder="Click to pick location"
                      className="flex-1 px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all cursor-pointer"
                    />
                    <button 
                      onClick={() => setIsMapModalOpen(true)}
                      className="p-2.5 bg-primary/10 text-primary rounded-xl hover:bg-primary/20 transition-colors"
                    >
                      <MapPin className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => setIsEditingOrgDetails(false)} className="px-6 py-2 rounded-xl font-bold uppercase text-xs hover:bg-muted">Cancel</button>
                <button onClick={handleUpdateOrg} className="bg-primary text-primary-foreground px-8 py-2 rounded-xl font-bold uppercase text-xs shadow-lg">Update Organization</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modules Modal */}
      {isEditingModules && (
        <div className="fixed inset-0 bg-black/5 z-[60] flex items-center justify-center p-4">
          <div className="bg-white border rounded-3xl shadow-2xl w-full max-w-4xl animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold">Assign Modules</h3>
              <button onClick={() => setIsEditingModules(false)} className="p-2 hover:bg-muted rounded-full">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-8 max-h-[70vh] overflow-y-auto">
              {MODULES.map(module => (
                <div key={module.id} className="space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <div className="p-1.5 bg-primary/10 text-primary rounded-lg">
                      {module.icon}
                    </div>
                    <h4 className="font-bold text-sm uppercase tracking-wider">{module.label}</h4>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {/* Main Module Checkbox */}
                    <label 
                      className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${
                        newOrg.assignedModules?.includes(module.id) 
                          ? 'bg-primary/10 border-primary shadow-sm' 
                          : 'hover:bg-muted'
                      }`}
                    >
                      <input 
                        type="checkbox"
                        checked={newOrg.assignedModules?.includes(module.id)}
                        onChange={(e) => {
                          const modules = newOrg.assignedModules || [];
                          if (e.target.checked) {
                            setNewOrg({ ...newOrg, assignedModules: [...modules, module.id] });
                          } else {
                            setNewOrg({ ...newOrg, assignedModules: modules.filter(id => id !== module.id) });
                          }
                        }}
                        className="hidden"
                      />
                      <div className="relative">
                        <div className={`p-2 rounded-lg ${newOrg.assignedModules?.includes(module.id) ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                          {module.icon}
                        </div>
                        {newOrg.assignedModules?.includes(module.id) && (
                          <div className="absolute -top-1 -right-1 text-green-500 bg-background rounded-full">
                            <CheckCircle2 className="w-3 h-3" />
                          </div>
                        )}
                      </div>
                      <span className="text-sm font-bold">{module.label} (Main)</span>
                    </label>

                    {/* Sub-modules Checkboxes */}
                    {module.subModules.map(sub => (
                      <label 
                        key={sub.id} 
                        className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${
                          newOrg.assignedModules?.includes(sub.id) 
                            ? 'bg-primary/10 border-primary shadow-sm' 
                            : 'hover:bg-muted'
                        }`}
                      >
                        <input 
                          type="checkbox"
                          checked={newOrg.assignedModules?.includes(sub.id)}
                          onChange={(e) => {
                            const modules = newOrg.assignedModules || [];
                            if (e.target.checked) {
                              setNewOrg({ ...newOrg, assignedModules: [...modules, sub.id] });
                            } else {
                              setNewOrg({ ...newOrg, assignedModules: modules.filter(id => id !== sub.id) });
                            }
                          }}
                          className="hidden"
                        />
                        <div className="relative">
                          <div className={`p-2 rounded-lg ${newOrg.assignedModules?.includes(sub.id) ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                            {module.icon}
                          </div>
                          {newOrg.assignedModules?.includes(sub.id) && (
                            <div className="absolute -top-1 -right-1 text-green-500 bg-background rounded-full">
                              <CheckCircle2 className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                        <span className="text-sm font-medium">{sub.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-6 border-t bg-muted/30 flex justify-end gap-3">
              <button 
                onClick={() => setIsEditingModules(false)} 
                className="px-6 py-2.5 rounded-xl font-bold uppercase text-xs hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleUpdateOrg} 
                className="bg-primary text-primary-foreground px-8 py-2.5 rounded-xl font-bold uppercase text-xs shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Billing Details Modal */}
      {isEditingBillingDetails && (
        <div className="fixed inset-0 bg-black/5 z-[60] flex items-center justify-center p-4">
          <div className="bg-white border rounded-3xl shadow-2xl w-full max-w-lg animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold">Edit Billing Details</h3>
              <button onClick={() => setIsEditingBillingDetails(false)} className="p-2 hover:bg-muted rounded-full">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase opacity-50">Select Product</label>
                <select 
                  value={newOrg.subscription?.productId || ''}
                  onChange={(e) => setNewOrg({ 
                    ...newOrg, 
                    subscription: { 
                      ...newOrg.subscription!, 
                      productId: e.target.value,
                      packageId: '' // Reset package when product changes
                    } 
                  })}
                  className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">Select a Product/Service</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.type})</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold uppercase opacity-50">Select Package</label>
                <select 
                  value={newOrg.subscription?.packageId || ''}
                  onChange={(e) => handlePackageChange(e.target.value)}
                  className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20"
                  disabled={!newOrg.subscription?.productId}
                >
                  <option value="">Select a Package</option>
                  {products
                    .find(p => p.id === newOrg.subscription?.productId)
                    ?.packages.map(pkg => (
                      <option key={pkg.name} value={pkg.name}>{pkg.name} - ${pkg.price}</option>
                    ))
                  }
                </select>
                {!newOrg.subscription?.productId && (
                  <p className="text-[10px] text-muted-foreground mt-1 font-medium italic">Please select a product first to see available packages.</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold uppercase opacity-50">Purchase Date</label>
                <input 
                  type="date"
                  value={newOrg.subscription?.purchaseDate || ''}
                  onChange={(e) => setNewOrg({ ...newOrg, subscription: { ...newOrg.subscription!, purchaseDate: e.target.value } })}
                  className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase opacity-50">Warning Date</label>
                <input 
                  type="date"
                  value={newOrg.subscription?.warningDate || ''}
                  onChange={(e) => setNewOrg({ ...newOrg, subscription: { ...newOrg.subscription!, warningDate: e.target.value } })}
                  className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase opacity-50">Renewal Date</label>
                <input 
                  type="date"
                  value={newOrg.subscription?.renewalDate || ''}
                  onChange={(e) => setNewOrg({ ...newOrg, subscription: { ...newOrg.subscription!, renewalDate: e.target.value } })}
                  className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => setIsEditingBillingDetails(false)} className="px-6 py-2 rounded-xl font-bold uppercase text-xs hover:bg-muted">Cancel</button>
                <button onClick={handleUpdateOrg} className="bg-primary text-primary-foreground px-8 py-2 rounded-xl font-bold uppercase text-xs shadow-lg">Update Billing Details</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeSubModule === 'users' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Users className="w-6 h-6" />
              Users Management
            </h2>
            <button 
              onClick={() => {
                setIsAddingUser(true);
                setEditingUser(null);
                setNewUser({ fullName: '', email: '', nationality: '', countryCode: '+20', phoneNumber: '', gender: 'male', password: '', memberships: [] });
              }}
              className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-bold uppercase"
            >
              <Plus className="w-4 h-4" />
              Create User
            </button>
          </div>

          {isAddingUser && (
            <div className="fixed inset-0 bg-black/5 z-50 flex items-center justify-center p-4">
              <div className="bg-white border rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
                <div className="p-6 border-b flex items-center justify-between bg-muted/30">
                  <div>
                    <h3 className="text-xl font-bold">{editingUser ? 'Edit User' : 'Create New User'}</h3>
                    <p className="text-sm text-muted-foreground">Manage user details and organization access</p>
                  </div>
                  <button 
                    onClick={() => setIsAddingUser(false)}
                    className="p-2 hover:bg-muted rounded-full transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-8 space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Full Name *</label>
                      <input 
                        type="text"
                        value={newUser.fullName || ''}
                        onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })}
                        placeholder="John Doe"
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Email Address *</label>
                      <input 
                        type="email"
                        value={newUser.email || ''}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        placeholder="john@example.com"
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                      />
                    </div>
                    {isLocalSession() ? (
                      <div className="space-y-2 md:col-span-2">
                        <label className="text-xs font-bold uppercase opacity-50">{editingUser ? 'New Password (optional)' : 'Password *'}</label>
                        <input
                          type="password"
                          value={newUser.password}
                          onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                          placeholder={editingUser ? 'Leave blank to keep the current password' : 'At least 8 characters'}
                          autoComplete="new-password"
                          className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                        />
                        <p className="text-xs text-muted-foreground">
                          {editingUser ? 'Enter a password only if you want to change it.' : 'The user will use this password to sign in.'}
                        </p>
                      </div>
                    ) : (
                      <div className="md:col-span-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-xs text-amber-700">
                        Password management is available in local account mode only.
                      </div>
                    )}
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Nationality</label>
                      <select 
                        value={newUser.nationality || ''}
                        onChange={(e) => setNewUser({ ...newUser, nationality: e.target.value })}
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                      >
                        <option value="">Select Nationality</option>
                        {countries.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Gender</label>
                      <select 
                        value={newUser.gender || 'male'}
                        onChange={(e) => setNewUser({ ...newUser, gender: e.target.value as 'male' | 'female' })}
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                      >
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs font-bold uppercase opacity-50">Phone Number</label>
                      <div className="flex gap-2">
                        <input 
                          type="text"
                          value={newUser.countryCode || '+20'}
                          onChange={(e) => setNewUser({ ...newUser, countryCode: e.target.value })}
                          placeholder="+20"
                          className="w-20 px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                        />
                        <div className="flex-1 space-y-1">
                          <input 
                            type="tel"
                            value={newUser.phoneNumber || ''}
                            onChange={(e) => setNewUser({ ...newUser, phoneNumber: e.target.value })}
                            placeholder="1234567890"
                            className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                          />
                          {newUser.phoneNumber.length > 0 && newUser.phoneNumber.length < 10 && (
                            <p className="text-[10px] text-destructive font-bold">
                              {10 - newUser.phoneNumber.length} digits missing
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4 p-6 border rounded-3xl bg-muted/20 md:col-span-2">
                      <h4 className="text-lg font-bold">Organization Membership</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-xs font-bold opacity-50">Select Organization</label>
                          <select 
                            value={currentMembership.orgId || ''}
                            onChange={(e) => setCurrentMembership({ ...currentMembership, orgId: e.target.value })}
                            className="w-full px-4 py-3 border rounded-xl bg-background"
                          >
                            <option value="">Select Organization</option>
                            {organizations.map(org => (
                              <option key={org.id} value={org.id}>{org.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-bold opacity-50">Access Level</label>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { id: 'super-admin', label: 'Super Admin' },
                              { id: 'owner', label: 'Owner' },
                              { id: 'admin', label: 'Admin' },
                              { id: 'user', label: 'User' }
                            ].map(role => (
                              <button
                                key={role.id}
                                onClick={() => setCurrentMembership({ ...currentMembership, role: role.id as any })}
                                className={`px-4 py-3 rounded-xl text-xs font-bold transition-all ${
                                  currentMembership.role === role.id ? 'bg-primary text-primary-foreground shadow-lg' : 'bg-background border'
                                }`}
                              >
                                {role.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-xs font-bold opacity-50">Permissions</label>
                        <label className="flex items-center gap-2 mb-2 text-xs font-bold">
                          <input
                            type="checkbox"
                            checked={currentMembership.permissions.length === MODULES.flatMap(m => [m.id, ...m.subModules.map(sm => sm.id)]).length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setCurrentMembership({ ...currentMembership, permissions: MODULES.flatMap(m => [m.id, ...m.subModules.map(sm => sm.id)]) });
                              } else {
                                setCurrentMembership({ ...currentMembership, permissions: [] });
                              }
                            }}
                          />
                          Assign All Modules
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                          {MODULES.flatMap(m => [
                            { id: m.id, label: m.label, isParent: true },
                            ...m.subModules.map(sm => ({ ...sm, isParent: false }))
                          ]).map(module => (
                            <button
                              key={module.id}
                              onClick={() => {
                                const perms = currentMembership.permissions.includes(module.id)
                                  ? currentMembership.permissions.filter(p => p !== module.id)
                                  : [...currentMembership.permissions, module.id];
                                setCurrentMembership({ ...currentMembership, permissions: perms });
                              }}
                              className={`relative px-3 py-3 rounded-xl border text-[10px] font-bold transition-all flex flex-col items-center justify-center gap-1 text-center ${
                                currentMembership.permissions.includes(module.id) 
                                  ? 'bg-primary/10 border-primary text-primary shadow-sm' 
                                  : 'bg-background hover:bg-muted'
                              } ${module.isParent ? 'border-primary/30' : 'opacity-80'}`}
                            >
                              {currentMembership.permissions.includes(module.id) && (
                                <div className="absolute -top-2 -right-2 bg-white rounded-full shadow-md text-green-600 p-0.5 z-10">
                                  <CheckCircle2 className="w-4 h-4" />
                                </div>
                              )}
                              <span className={module.isParent ? 'font-black' : ''}>{module.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          if (!currentMembership.orgId) return;
                          
                          let finalPermissions: string[] = [];
                          if (currentMembership.role === 'super-admin') {
                            finalPermissions = MODULES.flatMap(m => [m.id, ...m.subModules.map(sm => sm.id)]);
                          } else if (currentMembership.role === 'owner') {
                            finalPermissions = MODULES.flatMap(m => [m.id, ...m.subModules.map(sm => sm.id)]);
                          } else if (currentMembership.role === 'admin') {
                            // Admin gets a subset of permissions, e.g., everything except settings/super-admin stuff
                            finalPermissions = MODULES.filter(m => m.id !== 'settings').flatMap(m => [m.id, ...m.subModules.map(sm => sm.id)]);
                          } else {
                            finalPermissions = currentMembership.permissions;
                          }
                          
                          setNewUser({
                            ...newUser,
                            memberships: [...(newUser.memberships || []), { ...currentMembership, permissions: finalPermissions }]
                          });
                          setCurrentMembership({ orgId: '', role: 'user', permissions: ['dashboard'] });
                        }}
                        className="w-full py-2 bg-primary/10 text-primary rounded-xl text-xs font-bold hover:bg-primary/20 transition-all"
                      >
                        Add Membership
                      </button>
                    </div>

                    {(newUser.memberships || []).length > 0 && (
                      <div className="space-y-2">
                        <label className="text-xs font-bold uppercase opacity-50">Current Memberships</label>
                        <div className="space-y-2">
                          {(newUser.memberships || []).map((m, idx) => {
                            const org = organizations.find(o => o.id === m.orgId);
                            return (
                              <div key={idx} className="flex items-center justify-between p-3 border rounded-xl bg-background">
                                <div>
                                  <p className="text-sm font-bold">{org?.name}</p>
                                  <p className="text-[10px] text-muted-foreground uppercase">{m.role} • {(m.permissions || []).length} Permissions</p>
                                </div>
                                <button
                                  onClick={() => {
                                    const filtered = (newUser.memberships || []).filter((_, i) => i !== idx);
                                    setNewUser({ ...newUser, memberships: filtered });
                                  }}
                                  className="p-2 text-destructive hover:bg-destructive/10 rounded-lg"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-6 border-t bg-muted/30 flex justify-end gap-4">
                  <button 
                    onClick={() => setIsAddingUser(false)}
                    className="px-6 py-2.5 rounded-xl font-bold uppercase text-xs hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleAddUser}
                    className="bg-primary text-primary-foreground px-8 py-2.5 rounded-xl font-bold uppercase text-xs shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all"
                  >
                    {editingUser ? 'Update User' : 'Create User'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {orgUsers
              .filter(user => {
                const searchLower = searchTerm.toLowerCase();
                return user.fullName.toLowerCase().includes(searchLower) || 
                       user.email.toLowerCase().includes(searchLower);
              })
              .map(user => (
              <div key={user.id} className="p-4 border rounded-2xl bg-card flex items-center justify-between group hover:border-primary/50 transition-all">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${user.suspended ? 'bg-destructive/20 text-destructive' : 'bg-primary/10 text-primary'}`}>
                    {(user.fullName || user.email || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <h3 className={`font-bold ${user.suspended ? 'line-through opacity-50' : ''}`}>{user.fullName}</h3>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {user.memberships?.map((m, idx) => {
                        const org = organizations.find(o => o.id === m.orgId);
                        return (
                          <span key={idx} className="px-1.5 py-0.5 rounded bg-primary/5 text-[9px] font-bold text-primary border border-primary/10">
                            {org?.name || 'Unknown'}: {m.role}
                          </span>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {user.suspended && (
                    <span className="px-2 py-1 rounded-lg text-[10px] font-bold uppercase bg-destructive/10 text-destructive">
                      Suspended
                    </span>
                  )}
                  <button 
                    onClick={() => handleToggleSuspendUser(user)}
                    className={`p-2 rounded-xl transition-colors ${user.suspended ? 'text-primary hover:bg-primary/10' : 'text-destructive hover:bg-destructive/10'}`}
                  >
                    <ShieldCheck className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleEditUser(user)}
                    className="p-2 hover:bg-primary/10 text-primary rounded-xl transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleDeleteUser(user.id)}
                    className="p-2 hover:bg-destructive/10 text-destructive rounded-xl transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeSubModule === 'products' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Package className="w-6 h-6" />
              Products & Services
            </h2>
            <button 
              onClick={() => {
                setIsAddingProduct(true);
                setEditingProduct(null);
                setNewProduct({ name: '', description: '', type: 'Product', packages: [{ name: '', price: 0, description: '', modules: [] }] });
              }}
              className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-bold uppercase"
            >
              <Plus className="w-4 h-4" />
              Add Product/Service
            </button>
          </div>

          {isAddingProduct && (
            <div className="fixed inset-0 bg-black/5 z-50 flex items-center justify-center p-4">
              <div className="bg-white border rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
                <div className="p-6 border-b flex items-center justify-between bg-muted/30">
                  <div>
                    <h3 className="text-xl font-bold">{editingProduct ? 'Edit Product' : 'New Product/Service'}</h3>
                    <p className="text-sm text-muted-foreground">Manage your offerings and pricing packages</p>
                  </div>
                  <button 
                    onClick={() => setIsAddingProduct(false)}
                    className="p-2 hover:bg-muted rounded-full transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-8 space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Name</label>
                      <input 
                        type="text"
                        value={newProduct.name}
                        onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                        placeholder="Premium Subscription"
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Type</label>
                      <select 
                        value={newProduct.type}
                        onChange={(e) => setNewProduct({ ...newProduct, type: e.target.value as any })}
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                      >
                        <option value="Product">Product</option>
                        <option value="Service">Service</option>
                      </select>
                    </div>
                    <div className="md:col-span-2 space-y-2">
                      <label className="text-xs font-bold uppercase opacity-50">Description</label>
                      <textarea 
                        value={newProduct.description}
                        onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                        placeholder="Describe the product or service..."
                        className="w-full px-4 py-2.5 border rounded-xl bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all min-h-[100px]"
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold uppercase opacity-50">Packages</label>
                      <button 
                        onClick={() => setNewProduct({ ...newProduct, packages: [...newProduct.packages, { name: '', price: 0, description: '', modules: [] }] })}
                        className="text-primary text-xs font-bold flex items-center gap-1 hover:underline"
                      >
                        <Plus className="w-3 h-3" /> Add Package
                      </button>
                    </div>
                    <div className="space-y-4">
                      {newProduct.packages.map((pkg, idx) => (
                        <div key={idx} className="p-4 border rounded-2xl bg-muted/10 space-y-4 relative group">
                          <button 
                            onClick={() => {
                              const pkgs = newProduct.packages.filter((_, i) => i !== idx);
                              setNewProduct({ ...newProduct, packages: pkgs });
                            }}
                            className="absolute top-2 right-2 p-1 text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-[10px] font-bold uppercase opacity-50">Package Name</label>
                              <input 
                                type="text"
                                value={pkg.name}
                                onChange={(e) => {
                                  const pkgs = [...newProduct.packages];
                                  pkgs[idx].name = e.target.value;
                                  setNewProduct({ ...newProduct, packages: pkgs });
                                }}
                                placeholder="Basic Plan"
                                className="w-full px-3 py-2 border rounded-lg bg-background text-sm outline-none focus:ring-2 focus:ring-primary/20"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-[10px] font-bold uppercase opacity-50">Price</label>
                              <input 
                                type="number"
                                value={isNaN(pkg.price) ? '' : pkg.price}
                                onChange={(e) => {
                                  const pkgs = [...newProduct.packages];
                                  pkgs[idx].price = parseFloat(e.target.value) || 0;
                                  setNewProduct({ ...newProduct, packages: pkgs });
                                }}
                                placeholder="99.99"
                                className="w-full px-3 py-2 border rounded-lg bg-background text-sm outline-none focus:ring-2 focus:ring-primary/20"
                              />
                            </div>
                            <div className="md:col-span-2 space-y-2">
                              <label className="text-[10px] font-bold uppercase opacity-50">Package Description</label>
                              <input 
                                type="text"
                                value={pkg.description}
                                onChange={(e) => {
                                  const pkgs = [...newProduct.packages];
                                  pkgs[idx].description = e.target.value;
                                  setNewProduct({ ...newProduct, packages: pkgs });
                                }}
                                placeholder="What's included in this package?"
                                className="w-full px-3 py-2 border rounded-lg bg-background text-sm outline-none focus:ring-2 focus:ring-primary/20"
                              />
                            </div>
                            <div className="md:col-span-2 space-y-2">
                              <label className="text-[10px] font-bold uppercase opacity-50">Assigned Modules</label>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {MODULES.map(module => (
                                  <label key={module.id} className="flex items-center gap-2 p-2 border rounded-lg bg-background hover:bg-muted/50 cursor-pointer transition-colors">
                                    <input 
                                      type="checkbox"
                                      checked={pkg.modules?.includes(module.id)}
                                      onChange={(e) => {
                                        const pkgs = [...newProduct.packages];
                                        const modules = pkgs[idx].modules || [];
                                        if (e.target.checked) {
                                          // Add module and all sub-modules by default? Or just module?
                                          // Let's just add the module and all its sub-modules to make it easy
                                          const subModuleIds = module.subModules.map(sm => sm.id);
                                          pkgs[idx].modules = Array.from(new Set([...modules, module.id, ...subModuleIds]));
                                        } else {
                                          const subModuleIds = module.subModules.map(sm => sm.id);
                                          pkgs[idx].modules = modules.filter(id => id !== module.id && !subModuleIds.includes(id));
                                        }
                                        setNewProduct({ ...newProduct, packages: pkgs });
                                      }}
                                      className="w-3 h-3 text-primary rounded focus:ring-primary/20"
                                    />
                                    <span className="text-[10px] font-bold truncate">{module.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t bg-muted/30 flex justify-end gap-4">
                  <button 
                    onClick={() => setIsAddingProduct(false)}
                    className="px-6 py-2.5 rounded-xl font-bold uppercase text-xs hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleAddProduct}
                    className="bg-primary text-primary-foreground px-8 py-2.5 rounded-xl font-bold uppercase text-xs shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all"
                  >
                    {editingProduct ? 'Update Product' : 'Save Product'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products
              .filter(product => {
                const searchLower = searchTerm.toLowerCase();
                return product.name.toLowerCase().includes(searchLower) || 
                       product.description.toLowerCase().includes(searchLower);
              })
              .map(product => (
              <div 
                key={product.id} 
                className="group relative p-6 border rounded-2xl bg-card hover:border-primary/50 hover:shadow-lg transition-all space-y-4 cursor-pointer"
                onClick={() => setSelectedProductProfile(product)}
              >
                <div className="flex items-center justify-between">
                  <div className="p-2 bg-primary/10 text-primary rounded-lg">
                    {product.type === 'Product' ? <Package className="w-5 h-5" /> : <ShieldCheck className="w-5 h-5" />}
                  </div>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <button 
                      onClick={() => {
                        setEditingProduct(product);
                        setNewProduct({
                          name: product.name || '',
                          description: product.description || '',
                          type: (product.type as any) || 'Product',
                          packages: product.packages || [{ name: '', price: 0, description: '', modules: [] }]
                        });
                        setIsAddingProduct(true);
                      }}
                      className="p-2 hover:bg-primary/10 text-primary rounded-xl transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDeleteProduct(product.id)}
                      className="p-2 hover:bg-destructive/10 text-destructive rounded-xl transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-lg group-hover:text-primary transition-colors">{product.name}</h3>
                  <p className="text-xs text-muted-foreground line-clamp-2">{product.description}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-[10px] font-bold uppercase opacity-50">Packages ({(product.packages || []).length})</p>
                  <div className="flex flex-wrap gap-2">
                    {(product.packages || []).map((pkg, i) => (
                      <span key={i} className="px-2 py-1 bg-muted rounded text-[10px] font-medium border border-border/50">
                        {pkg.name} - ${pkg.price}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity Log Modal */}
      {viewingActivityLicense && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-black p-8 rounded-[40px] shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-2xl font-bold italic serif tracking-tight">Activity Log</h3>
                <p className="text-xs text-muted-foreground font-mono mt-1">{viewingActivityLicense.key}</p>
              </div>
              <button 
                onClick={() => setViewingActivityLicense(null)}
                className="p-2 hover:bg-muted rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 pr-2 custom-scrollbar">
              {licenseActivities.length === 0 ? (
                <div className="text-center py-20 bg-muted/30 rounded-[32px] border border-dashed">
                  <Activity className="w-10 h-10 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-sm text-muted-foreground font-medium italic serif">No activity logs found for this key.</p>
                </div>
              ) : (
                licenseActivities.map((activity) => (
                  <div key={activity.id} className="p-4 rounded-2xl bg-muted/30 border border-border/50 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-xl ${
                        activity.status === 'Success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {activity.status === 'Success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm">{activity.deviceName}</span>
                          <span className="text-[10px] bg-muted px-2 py-0.5 rounded-full font-mono">{activity.ipAddress}</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1 font-medium capitalize">
                          {activity.details || activity.status}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-bold uppercase opacity-50">
                        {activity.timestamp?.toDate ? activity.timestamp.toDate().toLocaleDateString() : 'Just now'}
                      </p>
                      <p className="text-xs font-mono mt-0.5">
                        {activity.timestamp?.toDate ? activity.timestamp.toDate().toLocaleTimeString() : ''}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Reset License Confirm Modal */}
      {licenseToReset && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-black p-8 rounded-[40px] shadow-2xl w-full max-w-sm text-center"
          >
            <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-bold italic serif tracking-tight mb-2">Reset Devices?</h3>
            <p className="text-muted-foreground text-sm mb-8">
              This will disconnect all currently active devices for this license. Users will need to log in again.
            </p>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setLicenseToReset(null)}
                className="flex-1 py-3 px-4 bg-muted hover:bg-muted/80 text-foreground font-bold rounded-2xl transition-all"
              >
                Cancel
              </button>
              <button 
                onClick={() => handleResetLicense(licenseToReset)}
                className="flex-1 py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-2xl transition-all"
              >
                Yes, Reset
              </button>
            </div>
          </motion.div>
        </div>
      )}
      {/* Product Profile Modal */}
      {selectedProductProfile && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[50] flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
          >
            <div className="p-6 border-b flex items-center justify-between bg-muted/30">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 text-primary rounded-2xl">
                  {selectedProductProfile.type === 'Product' ? <Package className="w-8 h-8" /> : <ShieldCheck className="w-8 h-8" />}
                </div>
                <div>
                  <h3 className="text-2xl font-bold tracking-tight italic serif">{selectedProductProfile.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="px-2 py-0.5 rounded-full bg-primary/10 text-[10px] font-bold uppercase text-primary">
                      {selectedProductProfile.type}
                    </span>
                  </div>
                </div>
              </div>
              <button 
                onClick={() => setSelectedProductProfile(null)}
                className="p-2 hover:bg-muted rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
              {/* Quick Assignment */}
              <div className="p-6 rounded-[32px] bg-primary/5 border border-primary/10 space-y-4">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-primary text-primary-foreground rounded-xl">
                    <Building2 className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold uppercase tracking-tight">Assign to Organization</h4>
                </div>
                <div className="flex gap-3">
                  <select 
                    className="flex-1 bg-white border border-primary/20 p-3 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-primary/20"
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAssignProduct(e.target.value, selectedProductProfile.id);
                        e.target.value = ''; // Reset select
                      }
                    }}
                  >
                    <option value="">Select Organization to Assign...</option>
                    {organizations
                      .filter(org => org.subscription?.productId !== selectedProductProfile.id)
                      .map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                  </select>
                </div>
                <p className="text-[10px] text-muted-foreground italic px-2">
                  Selecting an organization will link this product to its subscription profile.
                </p>
              </div>

              {/* Assigned Organizations List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-black uppercase tracking-widest opacity-40">Assigned Organizations</h4>
                  <span className="text-[10px] bg-primary/10 text-primary px-2 py-1 rounded-full font-bold">
                    {organizations.filter(org => org.subscription?.productId === selectedProductProfile.id).length} Active
                  </span>
                </div>
                <div className="space-y-2">
                  {organizations.filter(org => org.subscription?.productId && org.subscription.productId === selectedProductProfile.id).length === 0 ? (
                    <p className="text-sm italic opacity-50 px-4">No organizations currently assigned this product.</p>
                  ) : (
                    organizations
                      .filter(org => org.subscription?.productId && org.subscription.productId === selectedProductProfile.id)
                      .map(org => (
                        <div key={org.id} className="flex items-center justify-between p-4 border rounded-2xl bg-background group hover:border-primary/50 transition-all">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-muted rounded-xl">
                              <Building2 className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="text-sm font-bold">{org.name}</p>
                              <p className="text-[10px] text-muted-foreground uppercase">
                                {org.subscription?.packageId} • Renew: {org.subscription?.renewalDate}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${
                              org.status === 'Active' ? 'bg-green-100 text-green-700' : 
                              org.status === 'Suspended' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {org.status}
                            </span>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button 
                                onClick={() => handleToggleSuspendOrg(org)}
                                className={cn(
                                  "p-2 rounded-xl transition-all",
                                  org.status === 'Suspended' ? "text-primary hover:bg-primary/10" : "text-amber-600 hover:bg-amber-50"
                                )}
                                title={org.status === 'Suspended' ? 'Activate Organization' : 'Suspend Organization'}
                              >
                                <ShieldCheck className="w-4 h-4" />
                              </button>
                              <button 
                                onClick={() => handleUnassignProduct(org.id)}
                                className="p-2 hover:bg-destructive/10 text-destructive rounded-xl transition-colors"
                                title="Unassign from Product"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                              <button 
                                className="p-2 hover:bg-primary/10 text-primary rounded-xl transition-colors"
                                onClick={() => {
                                  // Details view placeholder
                                }}
                              >
                                <ChevronRight className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="text-xs font-black uppercase tracking-widest opacity-40">Description</h4>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                  {selectedProductProfile.description || 'No description provided.'}
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-black uppercase tracking-widest opacity-40">Packages & Pricing</h4>
                  <span className="text-[10px] bg-muted px-2 py-1 rounded-full font-bold">
                    {(selectedProductProfile.packages || []).length} Available
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  {(selectedProductProfile.packages || []).length === 0 ? (
                    <div className="text-center py-12 bg-muted/20 rounded-3xl border-2 border-dashed">
                      <p className="text-sm italic opacity-50">No packages defined for this product.</p>
                    </div>
                  ) : (
                    selectedProductProfile.packages.map((pkg, idx) => (
                      <div key={idx} className="p-6 border rounded-3xl bg-muted/10 relative overflow-hidden group hover:border-primary/30 transition-all">
                        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                          <Tag className="w-16 h-16 rotate-12" />
                        </div>
                        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="space-y-1">
                            <h5 className="text-lg font-bold">{pkg.name}</h5>
                            <p className="text-sm text-muted-foreground">{pkg.description}</p>
                            {pkg.modules && pkg.modules.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {pkg.modules.map(modId => {
                                  const module = MODULES.find(m => m.id === modId);
                                  if (!module) return null;
                                  return (
                                    <span key={modId} className="px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[8px] font-bold uppercase">
                                      {module.label}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-black text-primary">${pkg.price}</p>
                            <p className="text-[10px] font-bold opacity-40 uppercase">Subscription Rate</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminView;
